import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { getCommunicationTemplateById, getCommunicationTemplates } from "../../services/communicationTemplateApi";
import {
  createCommunicationServerRow,
  getCommunicationServerRows,
  getObdPromptOptions,
  importPortalRecipientData,
  ObdPromptOption,
  updateCommunicationServerRow,
} from "../../services/communicationServerApi";
import Select from "react-select";
import { useDebouncedCallback } from "use-debounce";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore – types for this editor may not be present, but it's safe to use at runtime.
import Editor from "@monaco-editor/react";
import { Check, Copy, Play, Wand2 } from "lucide-react";
import { ActiveChannelStatusChip } from "./ActiveChannelStatusChip";

type AddCommunicationKind = "api" | "cron" | "voice";
type CronPreset = "daily" | "weekly" | "monthly" | "hourly" | "custom";

/** Config value persisted as `defaultRecipientCollection` / `channelCollection.*` */
type RecipientCollectionType = "user" | "candidate" | "approach";
/** Portal MongoDB collection name for imports / aggregation test */
type PortalDbCollectionName = "candidates" | "users" | "approaches";

const parseRecipientCollectionFromApi = (v: unknown): RecipientCollectionType => {
  if (v === "user") return "user";
  if (v === "approach") return "approach";
  return "candidate";
};

const recipientCollectionToPortalDb = (col: RecipientCollectionType): PortalDbCollectionName => {
  if (col === "user") return "users";
  if (col === "approach") return "approaches";
  return "candidates";
};

const isUniformChannelCollection = (
  row: { channelCollection?: Record<string, string | undefined> },
  col: RecipientCollectionType,
  keys: readonly string[],
): boolean => keys.every((k) => (row.channelCollection?.[k] ?? "candidate") === col);

const channelCollectionFromRow = (row: {
  channelCollection?: Record<string, string | undefined>;
}): RecipientCollectionType =>
  parseRecipientCollectionFromApi(row.channelCollection?.whatsapp ?? "candidate");

const cronStatusMatchForCollection = (col: RecipientCollectionType): Record<string, unknown> => {
  if (col === "user") return { user_status: "active" };
  if (col === "candidate") return { candidate_status: "active" };
  return {};
};

/** Voice (API): ordered steps — prompts and variables can be interleaved (exact playback order). */
type VoiceSequenceItem =
  | { id: string; kind: "prompt"; prompt: ObdPromptOption }
  | { id: string; kind: "variable"; variableName: string };

/** Default API field name for the nth variable in the sequence (chips show this until a custom name is set). */
function defaultVoiceApiVariableName(variableIndex: number): string {
  return `var_${variableIndex + 1}`;
}

function newVoiceSequenceItemId(): string {
  return `vs-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 11)}`;
}

/** Legacy promptVariables: pair each variable with the next prompt in the sequence if any, else the previous prompt. */
function buildPromptVariablesFromSequence(
  seq: VoiceSequenceItem[]
): { promptId: string; variableName: string }[] {
  const out: { promptId: string; variableName: string }[] = [];
  for (let i = 0; i < seq.length; i++) {
    const step = seq[i];
    if (step.kind !== "variable") continue;
    const raw = step.variableName.trim();
    if (!raw) continue;
    const nextPrompt = seq.slice(i + 1).find((s): s is Extract<VoiceSequenceItem, { kind: "prompt" }> => s.kind === "prompt");
    const prevPrompt = [...seq.slice(0, i)]
      .reverse()
      .find((s): s is Extract<VoiceSequenceItem, { kind: "prompt" }> => s.kind === "prompt");
    const target = nextPrompt ?? prevPrompt;
    if (target) {
      out.push({ promptId: String(target.prompt.value).trim(), variableName: raw });
    }
  }
  return out;
}

/**
 * TTS / OBD shape: same order as the UI sequence.
 * - Prompt rows: `value` = prompt id from the dropdown option (`ObdPromptOption.promptId` / `value`).
 * - Variable rows: `value` = "1", "2", "3", … in the order variable steps appear in the sequence (first var → "1", …).
 */
function buildTtsListFromSequence(seq: VoiceSequenceItem[]): Array<{
  ttsType: "prompt" | "variable";
  value: string;
  variableType: string;
}> {
  let variableIndex = 0;
  return seq.map((step) => {
    if (step.kind === "prompt") {
      const id = String(step.prompt.promptId ?? step.prompt.value).trim();
      return {
        ttsType: "prompt" as const,
        value: id,
        variableType: "",
      };
    }
    variableIndex += 1;
    return {
      ttsType: "variable" as const,
      value: String(variableIndex),
      variableType: "text",
    };
  });
}

const VOICE_VAR_NAME_RE = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

/** API may return `voice_call`, `Voice call`, etc. */
function normalizeCommunicationTypeFromApi(raw: unknown): "messaging_channels" | "voice_call" {
  const s = String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  if (s === "voice_call" || s === "voicecall") return "voice_call";
  return "messaging_channels";
}

function isVoiceCallCommunicationType(raw: unknown): boolean {
  return normalizeCommunicationTypeFromApi(raw) === "voice_call";
}

/** Channel keys for cron rows (mirrors in-component ChannelKey) */
type CronChannelKey = "whatsapp" | "sms" | "rcs" | "email" | "inApp";

/** Add flow: In-App is disabled; validation and pickers use this list only */
const ADD_FLOW_CHANNEL_KEYS = ["whatsapp", "sms", "rcs", "email"] as const;
type AddFlowRecipientChannelKey = (typeof ADD_FLOW_CHANNEL_KEYS)[number];

/**
 * Frontend-only: when `true`, SMS and RCS are hidden in the cron modal (WhatsApp + Email only).
 * Set to `false` to show SMS/RCS again — no API or worker changes needed.
 */
const CRON_MODAL_HIDE_SMS_AND_RCS_UI = true;

/**
 * Per-channel visibility in the cron UI only. SMS/RCS follow `CRON_MODAL_HIDE_SMS_AND_RCS_UI`.
 * Payloads and `channelCollection` still use full `ADD_FLOW_CHANNEL_KEYS` where required (see filter/mongo dialogs).
 */
const CRON_UI_CHANNELS_ENABLED: Record<AddFlowRecipientChannelKey, boolean> = {
  whatsapp: true,
  sms: !CRON_MODAL_HIDE_SMS_AND_RCS_UI,
  rcs: !CRON_MODAL_HIDE_SMS_AND_RCS_UI,
  email: true,
};
const CRON_FLOW_CHANNEL_KEYS = ADD_FLOW_CHANNEL_KEYS.filter((k) => CRON_UI_CHANNELS_ENABLED[k]);
const isAddFlowRecipientChannelKey = (v: string): v is AddFlowRecipientChannelKey =>
  v === "whatsapp" || v === "sms" || v === "rcs" || v === "email";

/** Parse template.params (or similar) into variable/column names for recipient CSV */
const extractParamNamesFromTemplateItem = (item: any): string[] => {
  const raw = item?.params ?? item?.templateParams ?? item?.variables ?? item?.placeholders;
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((p: unknown) => {
        if (typeof p === "string") return p.trim();
        if (p && typeof p === "object") {
          const o = p as Record<string, unknown>;
          return String(o.name ?? o.key ?? o.field ?? o.param ?? o.column ?? "").trim();
        }
        return "";
      })
      .filter(Boolean);
  }
  if (typeof raw === "object") {
    return Object.keys(raw as object).map((k) => k.trim()).filter(Boolean);
  }
  return [];
};

/** Max numeric index in WhatsApp-style `{{1}}`, `{{2}}`, … across template text fields. */
const maxPlaceholderIndexInText = (text: string): number => {
  let max = 0;
  const re = /\{\{\s*(\d+)\s*\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const n = parseInt(m[1], 10);
    if (Number.isFinite(n) && n > max) max = n;
  }
  return max;
};

/** Largest `n` such that `{{n}}` appears in header/body/footer (and common fallbacks). */
const getPlaceholderCountFromTemplateItem = (item: any): number => {
  const parts = [
    item?.header,
    item?.body,
    item?.footer,
    item?.subject,
    item?.html,
    item?.text,
  ]
    .filter((x) => x != null && String(x).trim() !== "")
    .map((x) => String(x));
  return parts.reduce((acc, text) => Math.max(acc, maxPlaceholderIndexInText(text)), 0);
};

const getTemplateBodyTextFromItem = (item: any): string => {
  const body = String(item?.body ?? item?.text ?? item?.html ?? "").trim();
  const header = String(item?.header ?? "").trim();
  const footer = String(item?.footer ?? "").trim();
  return [header, body, footer].filter(Boolean).join("\n\n");
};

type TemplateParamNamesMap = Record<"whatsapp" | "sms" | "rcs" | "email" | "inApp", string[]>;

const emptyTemplateParamNames = (): TemplateParamNamesMap => ({
  whatsapp: [],
  sms: [],
  rcs: [],
  email: [],
  inApp: [],
});

const coerceTemplateParamNamesFromApi = (raw: unknown): TemplateParamNamesMap => {
  const out = emptyTemplateParamNames();
  if (!raw || typeof raw !== "object") return out;
  const o = raw as Record<string, unknown>;
  for (const k of ["whatsapp", "sms", "rcs", "email", "inApp"] as const) {
    const v = o[k];
    if (Array.isArray(v)) out[k] = v.map((x) => String(x ?? ""));
  }
  return out;
};

const padTemplateParamNames = (arr: string[] | undefined, len: number): string[] => {
  const a = [...(arr ?? [])];
  while (a.length < len) a.push("");
  return a.slice(0, len);
};

const resolveParamSlotCount = (sel: { params?: string[]; placeholderCount?: number } | undefined): number => {
  if (!sel) return 0;
  const pl = sel.params?.length ?? 0;
  const ph = sel.placeholderCount ?? 0;
  return Math.max(pl, ph);
};

const buildTemplateParamNamesForChannel = (
  prev: string[] | undefined,
  sel: { params?: string[]; placeholderCount?: number } | undefined
): string[] => {
  const n = resolveParamSlotCount(sel);
  if (n <= 0) return [];
  const prevSafe = prev ?? [];
  const fromParams = sel?.params ?? [];
  return Array.from({ length: n }, (_, i) => {
    const p = prevSafe[i];
    if (p !== undefined && p !== null) return String(p);
    if (fromParams[i] != null) return String(fromParams[i]).trim();
    return "";
  });
};

const isMongoObjectId = (value: unknown) => /^[a-fA-F0-9]{24}$/.test(String(value ?? "").trim());

type ChannelTemplateItemForParams = { id: string; params?: string[]; placeholderCount?: number };
type ChannelTemplatesForParams = Record<CronChannelKey, { items: ChannelTemplateItemForParams[] }>;

type CronScheduleFormRow = {
  id: string;
  preset: CronPreset;
  time: string;
  weekDays: number[];
  monthDay: number;
  hourlyMinute: number;
  cronCustom: string;
  channelActive: Record<CronChannelKey, boolean>;
  channelTemplates: Record<CronChannelKey, string>;
  channelCollection: Record<CronChannelKey, RecipientCollectionType>;
  whatsappRoleFilter?: string[];
  /** Candidate/user gender filter for UI `filters` mode; `ALL` = no gender restriction. */
  recipientGenderFilter?: "ALL" | "Male" | "Female";
  /** Candidate marital status filter for UI `filters` mode; `ALL` = no marital status restriction. */
  recipientMaritalStatusFilter?: "ALL" | "Single" | "Married" | "Divorced" | "Widowed";
  /** Optional `YYYY-MM-DD` bounds for `createdAt` in IST (UI filters mode). Empty = no bound. */
  recipientCreatedFrom?: string;
  recipientCreatedTo?: string;
  /** Optional MongoDB aggregation pipeline (JSON) to customize how recipients are fetched for this schedule. */
  customAggregationPipeline?: string;
};

/** Resolves ordered column names: saved overrides, then template `params`, else `param_${i+1}` for each slot. */
const resolveEffectiveParamNamesForTemplate = (
  opt: ChannelTemplateItemForParams | undefined,
  override: string[] | undefined
): string[] => {
  if (!opt) return [];
  const fromParams = opt.params ?? [];
  const pc = opt.placeholderCount ?? 0;
  const n = Math.max(fromParams.length, pc);
  if (n === 0) return [];
  const ov = override ?? [];
  const out: string[] = [];
  for (let i = 0; i < n; i++) {
    const custom = (ov[i] ?? "").trim();
    if (custom) out.push(custom);
    else if (fromParams[i] != null && String(fromParams[i]).trim()) out.push(String(fromParams[i]).trim());
    else out.push(`param_${i + 1}`);
  }
  return out;
};

/** Variable names for one channel: union of `params` across all schedules using that channel, plus routing fields. */
const buildSuggestedRecipientColumnNamesForChannel = (
  cronFormRows: CronScheduleFormRow[],
  channelTemplates: ChannelTemplatesForParams,
  channel: AddFlowRecipientChannelKey,
  templateParamOverrides?: Partial<Record<AddFlowRecipientChannelKey, string[]>>
): string[] => {
  const seen = new Map<string, string>();
  const order: string[] = [];
  const add = (display: string) => {
    const t = display.trim();
    const lo = t.toLowerCase();
    if (!lo || seen.has(lo)) return;
    seen.set(lo, t);
    order.push(t);
  };
  let hasSelectedTemplate = false;
  for (const row of cronFormRows) {
    if (!row.channelActive[channel]) continue;
    const tid = row.channelTemplates[channel];
    if (!isMongoObjectId(tid)) continue;
    hasSelectedTemplate = true;
    const opt = channelTemplates[channel]?.items?.find((i) => i.id === tid);
    const effective = resolveEffectiveParamNamesForTemplate(opt, templateParamOverrides?.[channel]);
    if (effective.length > 0) {
      effective.forEach((p) => add(p));
    } else {
      if (channel === "email") add("email");
      if (channel === "whatsapp" || channel === "sms" || channel === "rcs") add("mobile");
    }
  }
  if (hasSelectedTemplate) {
    add("recipient_email");
    add("recipient_phone");
  }
  return order;
};

/** Union of template `params` across enabled channels on one schedule row — used to prefill `$project`. */
const collectParamNamesForCronScheduleRow = (
  row: CronScheduleFormRow,
  channelTemplates: ChannelTemplatesForParams,
  templateParamOverrides?: Record<CronChannelKey, string[]>
): string[] => {
  const seen = new Set<string>();
  const order: string[] = [];
  for (const ch of CRON_FLOW_CHANNEL_KEYS) {
    if (!row.channelActive[ch]) continue;
    const tid = row.channelTemplates[ch];
    if (!isMongoObjectId(tid)) continue;
    const opt = channelTemplates[ch]?.items?.find((i) => i.id === tid);
    const effective = resolveEffectiveParamNamesForTemplate(opt, templateParamOverrides?.[ch]);
    for (const p of effective) {
      const name = String(p).trim();
      if (!name || seen.has(name)) continue;
      seen.add(name);
      order.push(name);
    }
  }
  if (row.channelActive.email && !seen.has("email")) {
    seen.add("email");
    order.push("email");
  }
  return order;
};

/**
 * Resolved payload field names for each variable step (order preserved, duplicates skipped by case).
 * Empty `variableName` uses the same default as the rest of the voice flow (`var_1`, …).
 */
const collectVoiceSequenceVariableFieldNames = (seq: VoiceSequenceItem[]): string[] => {
  let vi = 0;
  const seenLo = new Set<string>();
  const out: string[] = [];
  for (const step of seq) {
    if (step.kind !== "variable") continue;
    const resolved = step.variableName.trim() || defaultVoiceApiVariableName(vi);
    vi += 1;
    const lo = resolved.toLowerCase();
    if (seenLo.has(lo)) continue;
    seenLo.add(lo);
    out.push(resolved);
  }
  return out;
};

/** Template params first, then voice fields — unique by case-insensitive key, order preserved. */
const mergeCronProjectFieldNames = (templateParams: string[], voiceFields: string[]): string[] => {
  const seenLo = new Set<string>();
  const order: string[] = [];
  const add = (name: string) => {
    const t = name.trim();
    if (!t) return;
    const lo = t.toLowerCase();
    if (seenLo.has(lo)) return;
    seenLo.add(lo);
    order.push(t);
  };
  for (const p of templateParams) add(p);
  for (const p of voiceFields) add(p);
  return order;
};

const buildVoiceCronProjectStage = (voiceFieldNames: string[]): Record<string, unknown> => {
  const project: Record<string, unknown> = { _id: 1, mobile: "$phone" };
  for (const field of voiceFieldNames) {
    if (field.trim().toLowerCase() === "mobile") continue;
    project[field] = 1;
  }
  return project;
};

const syncCronAggregationProjectForRow = (
  row: CronScheduleFormRow,
  channelTemplates: ChannelTemplatesForParams,
  voiceVariableFieldNames: string[] = [],
  templateParamOverrides?: Record<CronChannelKey, string[]>
): CronScheduleFormRow => {
  const raw = (row.customAggregationPipeline ?? "").trim();
  if (!raw) return row;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return row;
  }
  if (!Array.isArray(parsed)) return row;
  const pipeline = [...parsed] as Array<Record<string, unknown>>;
  const paramNames = mergeCronProjectFieldNames(
    collectParamNamesForCronScheduleRow(row, channelTemplates, templateParamOverrides),
    voiceVariableFieldNames
  );
  if (paramNames.length === 0) return row;
  const project =
    voiceVariableFieldNames.length > 0
      ? buildVoiceCronProjectStage(paramNames)
      : (() => {
          const base: Record<string, 1> = { _id: 1 };
          for (const p of paramNames) base[p] = 1;
          return base;
        })();
  const idx = pipeline.findIndex((s) => s && typeof s === "object" && "$project" in s);
  if (idx >= 0) pipeline[idx] = { $project: project };
  else pipeline.push({ $project: project });
  const nextPipeline = JSON.stringify(pipeline, null, 2);
  if (nextPipeline === row.customAggregationPipeline) return row;
  return {
    ...row,
    customAggregationPipeline: nextPipeline,
  };
};

type RecipientChannelSlice = {
  csvRaw: string;
  rows: Record<string, string>[];
  batchId: string | null;
};

const emptyRecipientChannelSlice = (): RecipientChannelSlice => ({
  csvRaw: "",
  rows: [],
  batchId: null,
});

const initialRecipientByChannel = (): Record<AddFlowRecipientChannelKey, RecipientChannelSlice> => ({
  whatsapp: emptyRecipientChannelSlice(),
  sms: emptyRecipientChannelSlice(),
  rcs: emptyRecipientChannelSlice(),
  email: emptyRecipientChannelSlice(),
});

const emptyCronChannelActive = (): Record<CronChannelKey, boolean> => ({
  whatsapp: false,
  sms: false,
  rcs: false,
  email: false,
  inApp: false,
});

const emptyCronChannelTemplates = (): Record<CronChannelKey, string> => ({
  whatsapp: "",
  sms: "",
  rcs: "",
  email: "",
  inApp: "",
});

const emptyCronChannelCollection = (): Record<CronChannelKey, RecipientCollectionType> => ({
  whatsapp: "candidate",
  sms: "candidate",
  rcs: "candidate",
  email: "candidate",
  inApp: "candidate",
});

const MAX_CRON_SCHEDULES = 24;

/** `YYYY-MM-DD` from `<input type="date" />` */
const CRON_CREATED_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Delay before calling template search API while typing in react-select */
const TEMPLATE_SEARCH_DEBOUNCE_MS = 400;

const newCronFormRow = (): CronScheduleFormRow => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
  preset: "daily",
  time: "09:00",
  weekDays: [1, 2, 3, 4, 5],
  monthDay: 1,
  hourlyMinute: 0,
  cronCustom: "0 9 * * *",
  channelActive: emptyCronChannelActive(),
  channelTemplates: emptyCronChannelTemplates(),
  channelCollection: emptyCronChannelCollection(),
  whatsappRoleFilter: ["ALL"],
  recipientGenderFilter: "ALL",
  recipientMaritalStatusFilter: "ALL",
  recipientCreatedFrom: "",
  recipientCreatedTo: "",
  customAggregationPipeline: "",
});

const parseTimeHHMM = (v: unknown): string => {
  const s = String(v ?? "").trim();
  if (!/^\d{1,2}:\d{2}$/.test(s)) return "09:00";
  const [h, m] = s.split(":").map((x) => Number(x));
  const hh = Math.min(23, Math.max(0, Number.isFinite(h) ? h : 9));
  const mm = Math.min(59, Math.max(0, Number.isFinite(m) ? m : 0));
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
};

const mapCronSchedulesFromApi = (raw: unknown): CronScheduleFormRow[] => {
  if (!Array.isArray(raw) || raw.length === 0) return [newCronFormRow()];
  const mapped = raw
    .map((entry): CronScheduleFormRow | null => {
      if (!entry || typeof entry !== "object") return null;
      const e = entry as Record<string, any>;
      const row = newCronFormRow();
      row.preset = (["daily", "weekly", "monthly", "hourly", "custom"].includes(String(e.preset))
        ? e.preset
        : "daily") as CronPreset;
      row.time = parseTimeHHMM(e.time);
      row.weekDays = Array.isArray(e.weekDays)
        ? e.weekDays.map((x: any) => Number(x)).filter((n: number) => n >= 1 && n <= 7)
        : row.weekDays;
      row.monthDay = Number.isFinite(Number(e.monthDay))
        ? Math.min(31, Math.max(1, Number(e.monthDay)))
        : row.monthDay;
      row.hourlyMinute = Number.isFinite(Number(e.hourlyMinute))
        ? Math.min(59, Math.max(0, Number(e.hourlyMinute)))
        : row.hourlyMinute;
      row.cronCustom = String(e.cronCustom ?? e.expression ?? row.cronCustom);
      row.channelActive = { ...row.channelActive, ...(e.channelActive ?? {}) };
      row.channelTemplates = { ...row.channelTemplates, ...(e.channelTemplates ?? {}) };
      row.channelCollection = { ...row.channelCollection, ...(e.channelCollection ?? {}) };
      ADD_FLOW_CHANNEL_KEYS.forEach((k) => {
        row.channelCollection[k] = parseRecipientCollectionFromApi(row.channelCollection[k]);
      });
      let rolesFromApi: string[] | undefined;
      let genderResolvedFromFilters = false;
      const f = e.filters;
      if (f && typeof f === "object") {
        const ft = (f as { type?: string }).type;
        const pipeline = (f as { pipeline?: unknown }).pipeline;
        if (ft === "aggregation" || (pipeline != null && ft !== "filters")) {
          if (pipeline != null) {
            row.customAggregationPipeline =
              typeof pipeline === "string" ? pipeline : JSON.stringify(pipeline, null, 2);
          }
        } else if (ft === "filters") {
          const roles = (f as { roles?: unknown }).roles;
          rolesFromApi = Array.isArray(roles) ? roles.map(String) : ["ALL"];
          if (Object.prototype.hasOwnProperty.call(f, "gender")) {
            genderResolvedFromFilters = true;
            const gs = String((f as { gender?: unknown }).gender ?? "ALL").trim();
            if (gs === "Male" || gs === "Female") row.recipientGenderFilter = gs;
            else row.recipientGenderFilter = "ALL";
          }
          if (Object.prototype.hasOwnProperty.call(f, "maritalStatus")) {
            const ms = String((f as { maritalStatus?: unknown }).maritalStatus ?? "ALL").trim();
            if (ms === "Single" || ms === "Married" || ms === "Divorced" || ms === "Widowed") {
              row.recipientMaritalStatusFilter = ms;
            } else {
              row.recipientMaritalStatusFilter = "ALL";
            }
          }
          if (Object.prototype.hasOwnProperty.call(f, "createdFrom")) {
            row.recipientCreatedFrom = String((f as { createdFrom?: unknown }).createdFrom ?? "")
              .trim()
              .slice(0, 10);
          }
          if (Object.prototype.hasOwnProperty.call(f, "createdTo")) {
            row.recipientCreatedTo = String((f as { createdTo?: unknown }).createdTo ?? "")
              .trim()
              .slice(0, 10);
          }
        }
      }
      if (rolesFromApi === undefined && Array.isArray(e.whatsappRoleFilter)) {
        rolesFromApi = e.whatsappRoleFilter;
      }
      row.whatsappRoleFilter = rolesFromApi ?? ["ALL"];
      if (!genderResolvedFromFilters) {
        const legacyG = e.recipientGenderFilter;
        if (legacyG === "Male" || legacyG === "Female") row.recipientGenderFilter = legacyG;
        else if (legacyG === "ALL") row.recipientGenderFilter = "ALL";
      }
      return row;
    })
    .filter((x): x is CronScheduleFormRow => Boolean(x));
  return mapped.length > 0 ? mapped : [newCronFormRow()];
};

/** First selected template id for this channel across schedules (same order as create merge). */
const firstCronTemplateIdForChannel = (
  rows: CronScheduleFormRow[],
  channel: AddFlowRecipientChannelKey
): string | undefined => {
  for (const row of rows) {
    if (!row.channelActive[channel]) continue;
    const tid = String(row.channelTemplates[channel] ?? "").trim();
    if (isMongoObjectId(tid)) return tid;
  }
  return undefined;
};

const weekdayUiToCron = (d: number) => (d === 7 ? 0 : d);

const buildCronFromRow = (row: CronScheduleFormRow): string | null => {
  const [hh, mm] = (row.time || "09:00").split(":").map((x) => parseInt(x, 10));
  const H = Number.isFinite(hh) ? Math.min(23, Math.max(0, hh)) : 9;
  const M = Number.isFinite(mm) ? Math.min(59, Math.max(0, mm)) : 0;
  switch (row.preset) {
    case "daily":
      return `${M} ${H} * * *`;
    case "weekly":
      if (!row.weekDays.length) return null;
      const days = [...new Set(row.weekDays.map(weekdayUiToCron))]
        .sort((a, b) => a - b)
        .join(",");
      return `${M} ${H} * * ${days}`;
    case "monthly": {
      const dom = Math.min(31, Math.max(1, row.monthDay || 1));
      return `${M} ${H} ${dom} * *`;
    }
    case "hourly": {
      const m = Math.min(59, Math.max(0, row.hourlyMinute || 0));
      return `${m} * * * *`;
    }
    case "custom": {
      const parts = row.cronCustom.trim().split(/\s+/).filter(Boolean);
      if (parts.length !== 5) return null;
      return parts.join(" ");
    }
    default:
      return null;
  }
};

const escapeRecipientCsvCell = (v: string) => {
  const s = String(v ?? "");
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

/** Rebuild CSV text from applied rows (e.g. after canceling an edit or opening the editor). */
const recipientRowsToCsv = (columns: string[], rows: Record<string, string>[]): string => {
  if (!columns.length || !rows.length) return "";
  const header = columns.map(escapeRecipientCsvCell).join(",");
  const body = rows.map((r) => columns.map((c) => escapeRecipientCsvCell(r[c] ?? "")).join(",")).join("\n");
  return `${header}\n${body}`;
};

const DEFAULT_COMMUNICATION_API_BASE_URL = "http://localhost:5010/api";

const normalizeApiBaseUrl = (raw: string | undefined): string => {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed) return DEFAULT_COMMUNICATION_API_BASE_URL;
  return trimmed.replace(/\/+$/, "");
};

const collectUniqueCaseInsensitive = (values: string[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of values) {
    const trimmed = String(value ?? "").trim();
    if (!trimmed) continue;
    const lo = trimmed.toLowerCase();
    if (seen.has(lo)) continue;
    seen.add(lo);
    out.push(trimmed);
  }
  return out;
};

const buildSampleCurlForCommunication = async (
  row: {
    communication: string;
    communicationKind?: "api" | "cron" | "manual";
    communicationType?: "messaging_channels" | "all_channels" | "voice_call";
    defaultRecipientCollection: RecipientCollectionType;
    whatsapp: "Active" | "Inactive" | "Paused";
    sms: "Active" | "Inactive" | "Paused";
    rcs: "Active" | "Inactive" | "Paused";
    email: "Active" | "Inactive" | "Paused";
    inApp: "Active" | "Inactive" | "Paused";
    templates: Record<"whatsapp" | "sms" | "rcs" | "email" | "inApp", string>;
    templateParamNames?: TemplateParamNamesMap;
    voiceCall?: {
      promptVariables?: Array<{ promptId?: unknown; variableName?: unknown }>;
      voiceSteps?: unknown;
    };
  },
): Promise<string> => {
  const baseUrl = normalizeApiBaseUrl(process.env.REACT_APP_TEMPLATES_API_URL);
  const nameQuery = `name=${encodeURIComponent(row.communication)}`;

  if (isVoiceCallCommunicationType(row.communicationType)) {
    const voiceCall = row.voiceCall;
    const voiceStepsRaw = voiceCall?.voiceSteps;
    const promptVariablesRaw = voiceCall?.promptVariables;
    const voiceStepsAsSequence = Array.isArray(voiceStepsRaw)
      ? voiceStepsRaw
          .map((step, idx) => {
            if (!step || typeof step !== "object" || Array.isArray(step)) return null;
            const s = step as Record<string, unknown>;
            const type = String(s.type ?? s.kind ?? "").trim().toLowerCase();
            if (type !== "variable") return null;
            return {
              id: `sample-${idx}`,
              kind: "variable" as const,
              variableName: String(s.variableName ?? "").trim(),
            };
          })
          .filter((item): item is Extract<VoiceSequenceItem, { kind: "variable" }> => item !== null)
      : [];
    const promptVariableNames = Array.isArray(promptVariablesRaw)
      ? promptVariablesRaw
          .map((item) => String(item?.variableName ?? "").trim())
          .filter(Boolean)
      : [];
    const voiceFieldNames = [
      ...collectVoiceSequenceVariableFieldNames(voiceStepsAsSequence),
      ...promptVariableNames,
    ].filter((value, idx, arr) => arr.findIndex((x) => x.toLowerCase() === value.toLowerCase()) === idx);
    const dataRow: Record<string, string> = { phone: "9876543210" };
    const sampleValues = ["Sample Name", "Sample Company", "Sample Role"];
    voiceFieldNames.forEach((field, idx) => {
      dataRow[field] = sampleValues[idx] ?? `sample_${field}`;
    });
    return [
      `curl -X POST "${baseUrl}/conversations/send?${nameQuery}" \\`,
      `  -H "Content-Type: application/json" \\`,
      `  --data-raw '${JSON.stringify(
        {
          channel: "call",
          data: [dataRow],
        },
        null,
        2
      )}'`,
    ].join("\n");
  }

  type CurlCh = "whatsapp" | "sms" | "rcs" | "email" | "inApp";
  const curlChannelOrder: CurlCh[] = ["whatsapp", "sms", "rcs", "email", "inApp"];
  const activePairs: { channel: CurlCh; id: string }[] = [];
  for (const ch of curlChannelOrder) {
    if (row[ch] !== "Active") continue;
    const id = String(row.templates[ch] ?? "").trim();
    if (isMongoObjectId(id)) activePairs.push({ channel: ch, id });
  }
  const templateDocs = await Promise.all(
    activePairs.map(async ({ id }) => {
      try {
        return await getCommunicationTemplateById(id);
      } catch {
        return null;
      }
    })
  );
  const requiredParamKeys = collectUniqueCaseInsensitive(
    activePairs.flatMap(({ channel }, idx) => {
      const doc = templateDocs[idx];
      if (!doc) return [];
      const opt: ChannelTemplateItemForParams = {
        id: String(doc?._id ?? ""),
        params: Array.isArray(doc?.params) ? (doc.params as unknown[]).map((p) => String(p ?? "").trim()) : [],
        placeholderCount: getPlaceholderCountFromTemplateItem(doc),
      };
      return resolveEffectiveParamNamesForTemplate(opt, row.templateParamNames?.[channel]);
    })
  );
  const suggestedAnchorField = requiredParamKeys.find((param) => {
    const key = param.trim().toLowerCase();
    return key === "email" || key === "phone" || key === "mobile" || key === "vendor" || key === "user_id";
  });
  const anchorField = suggestedAnchorField || requiredParamKeys[0] || "anchor";
  const anchorValue =
    anchorField.toLowerCase() === "email"
      ? "user@example.com"
      : anchorField.toLowerCase() === "phone" || anchorField.toLowerCase() === "mobile"
        ? "9876543210"
        : "sample_anchor_value";
  const dataRow: Record<string, string> = {
    [anchorField]: anchorValue,
  };
  requiredParamKeys.forEach((param, idx) => {
    if (param === anchorField) return;
    dataRow[param] = `sample_${param || idx + 1}`;
  });
  return [
    `curl -X POST "${baseUrl}/conversations/send?${nameQuery}&anchor=${encodeURIComponent(anchorField)}" \\`,
    `  -H "Content-Type: application/json" \\`,
    `  --data-raw '${JSON.stringify(
      {
        data: [dataRow],
      },
      null,
      2
    )}'`,
  ].join("\n");
};

const WEEKDAY_OPTIONS: { v: number; l: string }[] = [
  { v: 1, l: "Mon" },
  { v: 2, l: "Tue" },
  { v: 3, l: "Wed" },
  { v: 4, l: "Thu" },
  { v: 5, l: "Fri" },
  { v: 6, l: "Sat" },
  { v: 7, l: "Sun" },
];

const CommunicationServer: React.FC = () => {
  const navigate = useNavigate();
  type ChannelKey = "whatsapp" | "sms" | "rcs" | "email" | "inApp";
  type ChannelStatus = "Active" | "Inactive" | "Paused";
  type DeliveryMode = "all" | "fallback";
  type DayKey = string;
  type Templates = Record<ChannelKey, string>;
  type DayPlan = {
    enabled: boolean;
    channels: Record<ChannelKey, boolean>;
    templates: Templates;
  };
  type CommunicationRow = {
    communicationId?: string;
    communication: string;
    communicationKind?: "api" | "cron" | "manual";
    communicationType?: "messaging_channels" | "all_channels" | "voice_call";
    /** Master on/off; persisted as `enabled` on the server (cron runs only when true). */
    enabled: boolean;
    whatsapp: ChannelStatus;
    sms: ChannelStatus;
    rcs: ChannelStatus;
    email: ChannelStatus;
    inApp: ChannelStatus;
    templates: Templates;  
    deliveryMode: DeliveryMode;
    priority: ChannelKey[];
    multiDayEnabled: boolean;
    dayPlans: Record<DayKey, DayPlan>;
    cronSchedules?: any[];
    /** Portal source: users collection vs candidates (persisted as `defaultRecipientCollection`). */
    defaultRecipientCollection: RecipientCollectionType;
    conversational?: boolean;
    webhookRoute?: string;
    voiceCall?: {
      enabled?: boolean;
      promptIds?: unknown;
      promptVariables?: Array<{ promptId?: unknown; variableName?: unknown }>;
      voiceSteps?: unknown;
    };
    /** Ordered param/column names per channel (index ↔ {{1}}, {{2}}, …). Empty strings allowed. */
    templateParamNames?: TemplateParamNamesMap;
  };

  const baseTemplate = (label: string) => ({
    whatsapp: "WA_Default_Intro",
    sms: "SMS_Brief_Update",
    rcs: "RCS_Rich_Card_Default",
    email: "Email_Default_Notice",
    inApp: "InApp_General_Notification",
  });

  /** Template picker values use Mongo _id from the templates API */
  const isChosenTemplateId = (value: unknown) => /^[a-fA-F0-9]{24}$/.test(String(value ?? "").trim());

  const buildDayPlans = (
    statuses: Record<ChannelKey, ChannelStatus>,
    templates: Templates
  ): Record<DayKey, DayPlan> => {
    const defaultChannels = {
      whatsapp: statuses.whatsapp === "Active",
      sms: statuses.sms === "Active",
      rcs: statuses.rcs === "Active",
      email: statuses.email === "Active",
      inApp: statuses.inApp === "Active",
    };
    return {
      day1: { enabled: true, channels: { ...defaultChannels }, templates: { ...templates } },
    };
  };

  const createRow = (
    communication: string,
    statuses: Record<ChannelKey, ChannelStatus>,
    deliveryMode: DeliveryMode,
    priority: ChannelKey[]
  ): CommunicationRow => {
    const templates = baseTemplate(communication);
    return {
      communication,
      enabled: true,
      whatsapp: statuses.whatsapp,
      sms: statuses.sms,
      rcs: statuses.rcs,
      email: statuses.email,
      inApp: statuses.inApp,
      templates,
      templateParamNames: emptyTemplateParamNames(),
      deliveryMode,
      priority,
      multiDayEnabled: false,
      dayPlans: buildDayPlans(statuses, templates),
      defaultRecipientCollection: "candidate",
      conversational: false,
      webhookRoute: "",
    };
  };

  const syncCronRowsRecipientCollection = (v: RecipientCollectionType) => {
    setCronFormRows((prev) =>
      prev.map((r) => ({
        ...r,
        channelCollection: {
          whatsapp: v,
          sms: v,
          rcs: v,
          email: v,
          inApp: v,
        },
      }))
    );
  };

  const setAddModalRecipientCollection = (v: RecipientCollectionType) => {
    setAddDraft((prev) => (prev ? { ...prev, defaultRecipientCollection: v } : prev));
    syncCronRowsRecipientCollection(v);
    const portalSource = recipientCollectionToPortalDb(v);
    setRecipientPortalSourceByChannel((prev) => {
      const next = { ...prev };
      for (const ch of ADD_FLOW_CHANNEL_KEYS) {
        next[ch] = portalSource;
      }
      return next;
    });
  };

  const [rows, setRows] = useState<CommunicationRow[]>([]);
  const [isLoadingRows, setIsLoadingRows] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [addOpen, setAddOpen] = useState<boolean>(false);
  const [addDraft, setAddDraft] = useState<CommunicationRow | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  const [addKind, setAddKind] = useState<AddCommunicationKind>("api");
  /** For non-voice communication type, choose API/Cron execution mode from the form. */
  const [defaultSubKind, setDefaultSubKind] = useState<"api" | "cron">("api");
  /** When Communication type is "Voice call", whether it runs like API or Cron. */
  const [voiceSubKind, setVoiceSubKind] = useState<"api" | "cron">("api");
  const effectiveAddKind = useMemo((): "api" | "cron" => {
    if (addKind === "voice") return voiceSubKind;
    if (addKind === "cron") return "cron";
    return defaultSubKind;
  }, [addKind, defaultSubKind, voiceSubKind]);
  /**
   * Voice + cron: voice sequence + schedule + Mongo only (no WhatsApp/email template rows).
   * Use `addDraft.communicationType` as well as `addKind` so we still hide messaging UI if the API
   * sent a variant of `voice_call` and the form state briefly mismatches.
   */
  const isVoiceCronUi = useMemo(() => {
    if (effectiveAddKind !== "cron") return false;
    if (addKind === "voice") return true;
    return isVoiceCallCommunicationType(addDraft?.communicationType);
  }, [addKind, addDraft?.communicationType, effectiveAddKind]);
  /** Any voice communication uses the sequence builder (API or cron). */
  const isVoiceSequenceMode = useMemo(() => addKind === "voice", [addKind]);

  const [cronFormRows, setCronFormRows] = useState<CronScheduleFormRow[]>(() => [newCronFormRow()]);
  const [cronFilterRowId, setCronFilterRowId] = useState<string | null>(null);
  const [cronMongoRowId, setCronMongoRowId] = useState<string | null>(null);
  const [isTestingAggregation, setIsTestingAggregation] = useState<boolean>(false);
  const [aggregationTestResult, setAggregationTestResult] = useState<string | null>(null);
  const [aggregationTestOk, setAggregationTestOk] = useState<boolean | null>(null);
  const [aggregationTestCount, setAggregationTestCount] = useState<number | null>(null);
  const [showAggregationSample, setShowAggregationSample] = useState<boolean>(false);
  const [recipientByChannel, setRecipientByChannel] = useState<
    Record<AddFlowRecipientChannelKey, RecipientChannelSlice>
  >(() => initialRecipientByChannel());
  /** When false and rows exist, show table + "Re-import" instead of the import panel. */
  const [recipientCsvEditorOpenByChannel, setRecipientCsvEditorOpenByChannel] = useState<
    Partial<Record<AddFlowRecipientChannelKey, boolean>>
  >({});
  const [recipientParseHint, setRecipientParseHint] = useState<string | null>(null);
  /** Cron add flow: portal DB collection for recipient import (per channel). */
  const [recipientPortalSourceByChannel, setRecipientPortalSourceByChannel] = useState<
    Partial<Record<AddFlowRecipientChannelKey, PortalDbCollectionName>>
  >({});

  const getRecipientPortalSourceForChannel = (ch: AddFlowRecipientChannelKey): PortalDbCollectionName =>
    recipientPortalSourceByChannel[ch] ?? "candidates";

  const [uploadingRecipientChannel, setUploadingRecipientChannel] = useState<AddFlowRecipientChannelKey | null>(null);
  const lastCronRecipientAutoKeyByChannelRef = useRef<Partial<Record<AddFlowRecipientChannelKey, string>>>({});
  const [nameError, setNameError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [voiceCallEnabled, setVoiceCallEnabled] = useState<boolean>(false);
  const [voicePromptOptions, setVoicePromptOptions] = useState<ObdPromptOption[]>([]);
  const [isVoicePromptsLoading, setIsVoicePromptsLoading] = useState<boolean>(false);
  const [voicePromptSelections, setVoicePromptSelections] = useState<ObdPromptOption[]>([]);
  /** Voice + API: interleaved prompt & variable steps (exact order). */
  const [voiceSequence, setVoiceSequence] = useState<VoiceSequenceItem[]>([]);
  const voiceSequenceVariableFieldNames = useMemo(
    () => collectVoiceSequenceVariableFieldNames(voiceSequence),
    [voiceSequence]
  );
  /** Remount prompt picker after each add so the next choice is independent. */
  const [voicePromptPickerKey, setVoicePromptPickerKey] = useState(0);

  useEffect(() => {
    if (addKind !== "voice") {
      setVoiceSequence([]);
      setVoicePromptPickerKey(0);
    }
  }, [addKind]);

  useEffect(() => {
    if (addKind !== "voice") return;
    const ttsListBuilt = buildTtsListFromSequence(voiceSequence);
    console.log("[communicationServer] voiceCall ttsList", ttsListBuilt);
    console.log("[communicationServer] voiceCall ttsList JSON", JSON.stringify({ ttsList: ttsListBuilt }));
  }, [voiceSequence, addKind]);

  const initialCronTemplateSnapshotRef = useRef<string[][] | null>(null);
  const [showCronTemplateWarning, setShowCronTemplateWarning] = useState<boolean>(false);
  const [cronTemplateChangeAck, setCronTemplateChangeAck] = useState<boolean>(false);
  const [templateParamsMissingModal, setTemplateParamsMissingModal] = useState<{
    channelLabel: string;
    templateLabel: string;
    placeholderCount: number;
  } | null>(null);
  const [templateParamEditorModal, setTemplateParamEditorModal] = useState<{
    channelKey: AddFlowRecipientChannelKey;
    channelLabel: string;
    templateId: string;
    templateLabel: string;
    templateBody: string;
    slotCount: number;
  } | null>(null);
  const [togglingEnabledId, setTogglingEnabledId] = useState<string | null>(null);
  const toStatus = (value: any): ChannelStatus => {
    const status = String(value || "").toLowerCase();
    if (status === "active") return "Active";
    if (status === "paused") return "Paused";
    return "Inactive";
  };

  const toChannelKey = (value: string): ChannelKey | null => {
    const v = String(value || "").toLowerCase();
    if (v === "whatsapp") return "whatsapp";
    if (v === "sms") return "sms";
    if (v === "rcs") return "rcs";
    if (v === "email") return "email";
    if (v === "inapp" || v === "in-app" || v === "in_app") return "inApp";
    return null;
  };

  const loadRows = async (pageArg: number = page, pageSizeArg: number = pageSize, searchArg: string = searchQuery) => {
    try {
      setIsLoadingRows(true);
      const json = await getCommunicationServerRows({
        page: pageArg,
        pageSize: pageSizeArg,
        search: searchArg.trim() || undefined,
      });

      const rawRows = Array.isArray(json) ? json : Array.isArray((json as any)?.data) ? (json as any).data : [];

      const mappedRows: CommunicationRow[] = rawRows.map((item: any, idx: number) => {
        const statuses: Record<ChannelKey, ChannelStatus> = {
          whatsapp: toStatus(item?.whatsapp),
          sms: toStatus(item?.sms),
          rcs: toStatus(item?.rcs),
          email: toStatus(item?.email),
          inApp: toStatus(item?.inApp || item?.in_app || item?.inapp),
        };

        const itemTemplates = item?.templates || {};
        const coerceTemplate = (v: unknown) => (v === null || v === undefined ? "" : String(v).trim());
        const templates: Templates = {
          whatsapp: coerceTemplate(itemTemplates?.whatsapp),
          sms: coerceTemplate(itemTemplates?.sms),
          rcs: coerceTemplate(itemTemplates?.rcs),
          email: coerceTemplate(itemTemplates?.email),
          inApp: coerceTemplate(itemTemplates?.inApp ?? itemTemplates?.in_app),
        };

        const priorityFromApi: ChannelKey[] = Array.isArray(item?.priority)
          ? item.priority
              .map((x: string) => toChannelKey(x))
              .filter((x: ChannelKey | null): x is ChannelKey => Boolean(x))
          : [];

        const fallbackPriority: ChannelKey[] = ["whatsapp", "sms", "rcs", "email", "inApp"];
        const mergedPriority = [...priorityFromApi, ...fallbackPriority].filter(
          (key, index, arr) => arr.indexOf(key) === index
        );

        const deliveryMode: DeliveryMode = item?.deliveryMode === "fallback" ? "fallback" : "all";
        const communication = String(item?.communication || item?.name || item?.title || `Communication ${idx + 1}`);

        const defaultRow = createRow(communication, statuses, deliveryMode, mergedPriority);
        const hasDayPlans = item?.dayPlans && typeof item.dayPlans === "object";

        const communicationId =
          item?._id != null ? String(item._id) : item?.id != null ? String(item.id) : undefined;

        return {
          ...defaultRow,
          enabled: item?.enabled !== false,
          communicationId,
          communicationKind:
            item?.communicationKind === "cron"
              ? "cron"
              : item?.communicationKind === "manual"
                ? "manual"
                : "api",
          communicationType: normalizeCommunicationTypeFromApi(
            item?.communicationType ?? (item as { communication_type?: unknown })?.communication_type
          ),
          templates,
          multiDayEnabled: Boolean(item?.multiDayEnabled),
          dayPlans: hasDayPlans ? item.dayPlans : buildDayPlans(statuses, templates),
          cronSchedules: Array.isArray(item?.cronSchedules) ? item.cronSchedules : [],
          defaultRecipientCollection: parseRecipientCollectionFromApi(item?.defaultRecipientCollection),
          conversational: item?.conversational === true,
          webhookRoute: String(item?.webhookRoute ?? item?.conversationalWebhookRoute ?? "").trim(),
          voiceCall:
            item?.voiceCall && typeof item.voiceCall === "object" ? item.voiceCall : undefined,
          templateParamNames: coerceTemplateParamNamesFromApi(item?.templateParamNames),
        };
      });

      setRows(mappedRows);
      if (!Array.isArray(json) && json) {
        setTotal(typeof (json as any).total === "number" ? (json as any).total : mappedRows.length);
      } else {
        setTotal(mappedRows.length);
      }
    } catch (error) {
      setRows([]);
      setTotal(0);
    } finally {
      setIsLoadingRows(false);
    }
  };

  useEffect(() => {
    loadRows(page, pageSize, searchQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, searchQuery]);

  const keepOnlyDayOne = (row: CommunicationRow): CommunicationRow => {
    const day1Plan = row.dayPlans.day1;
    if (row.multiDayEnabled || !day1Plan) return row;
    return {
      ...row,
      dayPlans: { day1: day1Plan },
    };
  };

  /** Hydrate `voiceSequence` from saved `voiceCall` (used for voice + API and voice + cron). */
  const applyVoiceCallSequenceFromRow = (normalizedRow: CommunicationRow) => {
    const isVoiceType = isVoiceCallCommunicationType(normalizedRow.communicationType);
    const vc = normalizedRow.voiceCall;
    const pvars = vc && Array.isArray(vc.promptVariables) ? vc.promptVariables : [];
    const pids =
      vc && Array.isArray(vc.promptIds)
        ? vc.promptIds.map((x) => String(x).trim()).filter(Boolean)
        : [];
    const rawVoiceSteps = vc && Array.isArray((vc as { voiceSteps?: unknown }).voiceSteps)
      ? (vc as { voiceSteps: unknown[] }).voiceSteps
      : [];

    if (isVoiceType && rawVoiceSteps.length > 0) {
      setVoiceSequence(
        rawVoiceSteps.map((step: unknown, i: number) => {
          const o = step as Record<string, unknown>;
          const t = String(o?.type ?? o?.kind ?? "").toLowerCase();
          if (t === "prompt" || t === "voice_prompt") {
            const pid = String(o?.promptId ?? o?.id ?? "").trim();
            return {
              id: newVoiceSequenceItemId(),
              kind: "prompt" as const,
              prompt: { value: pid, label: pid, promptId: pid },
            };
          }
          if (t === "variable") {
            return {
              id: newVoiceSequenceItemId(),
              kind: "variable" as const,
              variableName: String(o?.variableName ?? defaultVoiceApiVariableName(i)),
            };
          }
          const pid = String(o?.promptId ?? "").trim();
          if (pid) {
            return {
              id: newVoiceSequenceItemId(),
              kind: "prompt" as const,
              prompt: { value: pid, label: pid, promptId: pid },
            };
          }
          return {
            id: newVoiceSequenceItemId(),
            kind: "variable" as const,
            variableName: String(o?.variableName ?? defaultVoiceApiVariableName(i)),
          };
        })
      );
    } else if (isVoiceType && pvars.length > 0) {
      const orderIds =
        pids.length > 0
          ? pids
          : pvars.map((p) => String(p?.promptId ?? "").trim()).filter(Boolean);
      const n = Math.max(orderIds.length, pvars.length);
      const seq: VoiceSequenceItem[] = [];
      for (let i = 0; i < n; i++) {
        if (i < orderIds.length) {
          const id = orderIds[i];
          seq.push({
            id: newVoiceSequenceItemId(),
            kind: "prompt",
            prompt: { value: id, label: id, promptId: id },
          });
        }
        if (i < pvars.length) {
          const pv = pvars[i];
          seq.push({
            id: newVoiceSequenceItemId(),
            kind: "variable",
            variableName: String(pv?.variableName ?? defaultVoiceApiVariableName(i)),
          });
        }
      }
      setVoiceSequence(seq);
    } else if (isVoiceType && pids.length > 0) {
      setVoiceSequence(
        pids.map((id) => ({
          id: newVoiceSequenceItemId(),
          kind: "prompt" as const,
          prompt: { value: id, label: id, promptId: id },
        }))
      );
    } else {
      setVoiceSequence([]);
    }
  };

  const openEditor = (idx: number) => {
    const selected = rows[idx];
    if (selected?.communicationKind === "cron") {
      const normalizedRow = keepOnlyDayOne(selected);
      const mappedCronRows = mapCronSchedulesFromApi(selected.cronSchedules);
      setAddDraft({
        ...normalizedRow,
        templates: { ...normalizedRow.templates },
        communicationId: normalizedRow.communicationId,
      });
      setNameError(null);
      setAddError(null);
      setCronFormRows(mappedCronRows);
      setCronTemplateChangeAck(false);
      setVoiceCallEnabled(false);
      setVoicePromptSelections([]);

      const isVoiceCronType = isVoiceCallCommunicationType(normalizedRow.communicationType);
      if (isVoiceCronType) {
        setAddKind("voice");
        setVoiceSubKind("cron");
        applyVoiceCallSequenceFromRow(normalizedRow);
        setVoicePromptPickerKey(0);
      } else {
        setAddKind("cron");
        setVoiceSubKind("api");
        setVoiceSequence([]);
        setVoicePromptPickerKey(0);
      }

      // Snapshot initial cron templates so we can detect changes on save.
      initialCronTemplateSnapshotRef.current = mappedCronRows.map((r) =>
        CRON_FLOW_CHANNEL_KEYS.map((ch) => String(r.channelTemplates[ch] ?? "").trim())
      );
      setAddOpen(true);
      if (isVoiceCronType) {
        void loadVoicePromptOptions();
      } else {
        for (const r of mappedCronRows) {
          for (const ch of CRON_FLOW_CHANNEL_KEYS) {
            const tid = String(r.channelTemplates[ch] ?? "").trim();
            if (!isMongoObjectId(tid)) continue;
            void ensureTemplateOptionForChannel(ch, tid);
          }
        }
      }
      return;
    }

    const normalizedRow = keepOnlyDayOne(selected);
    const vc = normalizedRow.voiceCall;
    const pvars = vc && Array.isArray(vc.promptVariables) ? vc.promptVariables : [];
    const pids =
      vc && Array.isArray(vc.promptIds)
        ? vc.promptIds.map((x) => String(x).trim()).filter(Boolean)
        : [];
    const rawVoiceSteps = vc && Array.isArray((vc as { voiceSteps?: unknown }).voiceSteps)
      ? (vc as { voiceSteps: unknown[] }).voiceSteps
      : [];

    setAddDraft({
      ...normalizedRow,
      templates: { ...normalizedRow.templates },
      communicationId: normalizedRow.communicationId,
    });
    /** Must match row kind: stale `defaultSubKind` would make `effectiveAddKind` wrong and block Save / PATCH. */
    setDefaultSubKind("api");
    setNameError(null);
    setAddError(null);
    setCronFormRows([newCronFormRow()]);
    setRecipientByChannel(initialRecipientByChannel());
    setRecipientCsvEditorOpenByChannel({});
    setRecipientParseHint(null);
    setRecipientPortalSourceByChannel({});
    setUploadingRecipientChannel(null);
    lastCronRecipientAutoKeyByChannelRef.current = {};
    initialCronTemplateSnapshotRef.current = null;
    setCronTemplateChangeAck(false);
    setVoiceCallEnabled(false);
    setVoicePromptSelections([]);

    const isVoiceType = isVoiceCallCommunicationType(normalizedRow.communicationType);
    if (isVoiceType) {
      setAddKind("voice");
      setVoiceSubKind("api");
      applyVoiceCallSequenceFromRow(normalizedRow);
    } else {
      setAddKind("api");
      setVoiceSubKind("api");
      setVoiceSequence([]);
    }

    setAddOpen(true);
    if (rawVoiceSteps.length > 0 || pvars.length > 0 || pids.length > 0) {
      void loadVoicePromptOptions();
    }
    for (const ch of channels) {
      const tid = String(normalizedRow.templates[ch.key] ?? "").trim();
      if (!isMongoObjectId(tid)) continue;
      void ensureTemplateOptionForChannel(ch.key, tid);
    }
  };

  const copySampleCurl = async (idx: number) => {
    const selected = rows[idx];
    if (!selected) {
      toast.error("Cannot copy sample curl for this row.");
      return;
    }
    try {
      const curl = await buildSampleCurlForCommunication(selected);
      await navigator.clipboard.writeText(curl);
      toast.success("Sample curl copied.");
    } catch {
      toast.error("Failed to copy sample curl.");
    }
  };

  const handleToggleRowEnabled = async (idx: number, nextEnabled: boolean) => {
    const row = rows[idx];
    const id = row?.communicationId?.trim();
    if (!id) {
      toast.error("Cannot update: this row has no server id.");
      return;
    }
    setTogglingEnabledId(id);
    try {
      await updateCommunicationServerRow(id, { enabled: nextEnabled });
      setRows((prev) =>
        prev.map((r, i) => (i === idx ? { ...r, enabled: nextEnabled } : r))
      );
      toast.success(nextEnabled ? "Communication enabled." : "Communication disabled.");
    } catch {
      /* error toast from API helper */
    } finally {
      setTogglingEnabledId(null);
    }
  };

  const openAdd = () => {
    const statuses: Record<ChannelKey, ChannelStatus> = {
      whatsapp: "Inactive",
      sms: "Inactive",
      rcs: "Inactive",
      email: "Inactive",
      inApp: "Inactive",
    };
    const priority: ChannelKey[] = ["whatsapp", "sms", "rcs", "email", "inApp"];
    const initialRow = createRow("", statuses, "all", priority);
    setAddDraft({
      ...initialRow,
      templates: {
        whatsapp: "",
        sms: "",
        rcs: "",
        email: "",
        inApp: "",
      },
      templateParamNames: emptyTemplateParamNames(),
    });
    setAddError(null);
    setNameError(null);
    setAddKind("api");
    setDefaultSubKind("api");
    setVoiceSubKind("api");
    setCronFormRows([newCronFormRow()]);
    setRecipientByChannel(initialRecipientByChannel());
    setRecipientCsvEditorOpenByChannel({});
    setRecipientParseHint(null);
    setRecipientPortalSourceByChannel({});
    setUploadingRecipientChannel(null);
    lastCronRecipientAutoKeyByChannelRef.current = {};
    initialCronTemplateSnapshotRef.current = null;
    setCronTemplateChangeAck(false);
    setVoiceCallEnabled(false);
    setVoicePromptSelections([]);
    setVoiceSequence([]);
    setVoicePromptPickerKey(0);
    setAddOpen(true);
  };

  const closeAdd = () => {
    setAddOpen(false);
    setAddDraft(null);
    setAddError(null);
    setTemplateParamEditorModal(null);
    setNameError(null);
    setAddKind("api");
    setDefaultSubKind("api");
    setVoiceSubKind("api");
    setCronFormRows([newCronFormRow()]);
    setRecipientByChannel(initialRecipientByChannel());
    setRecipientCsvEditorOpenByChannel({});
    setRecipientParseHint(null);
    setRecipientPortalSourceByChannel({});
    setUploadingRecipientChannel(null);
    lastCronRecipientAutoKeyByChannelRef.current = {};
    setVoiceCallEnabled(false);
    setVoicePromptSelections([]);
    setVoiceSequence([]);
    setVoicePromptPickerKey(0);
  };

  const validateCommunicationName = (value: string): string | null => {
    const trimmed = value.trim();
    if (!trimmed) return "Communication name is required.";
    if (trimmed.length < 3) return "Communication name must be at least 3 characters.";
    if (trimmed.length > 80) return "Communication name must be 80 characters or less.";
    if (!/^[A-Za-z0-9][A-Za-z0-9\s&\-_/()]*$/.test(trimmed)) {
      return "Use letters, numbers, spaces, and - _ / ( ) & only.";
    }
    return null;
  };

  const addCommunicationRow = async () => {
    if (!addDraft) return;
    const name = addDraft.communication.trim();
    const nameValidation = validateCommunicationName(name);
    if (nameValidation) {
      setNameError(nameValidation);
      setAddError(nameValidation);
      return;
    }
    setNameError(null);

    if (addDraft.conversational) {
      const whatsAppActive =
        effectiveAddKind === "cron"
          ? cronFormRows.some(
              (r) => r.channelActive.whatsapp && isChosenTemplateId(r.channelTemplates.whatsapp),
            )
          : addDraft.whatsapp === "Active";
      if (!whatsAppActive) {
        setAddError("Conversational mode is only available when WhatsApp is enabled.");
        return;
      }
    }

    const ordered: ChannelKey[] = ["whatsapp", "sms", "rcs", "email", "inApp"];

    if (addKind === "voice") {
      const promptCount = voiceSequence.filter((s) => s.kind === "prompt").length;
      if (promptCount === 0) {
        setAddError("Add at least one voice prompt to the sequence (pick a prompt and Add prompt).");
        return;
      }
      const namesLower: string[] = [];
      let vi = 0;
      for (const step of voiceSequence) {
        if (step.kind !== "variable") continue;
        vi += 1;
        const raw = step.variableName.trim();
        if (!raw) {
          setAddError(`Enter a name for variable step ${vi} in the sequence.`);
          return;
        }
        if (!VOICE_VAR_NAME_RE.test(raw)) {
          setAddError(
            `Variable "${raw}" must start with a letter or underscore and use only letters, numbers, and underscores.`
          );
          return;
        }
        const lo = raw.toLowerCase();
        if (namesLower.includes(lo)) {
          setAddError(`Variable names must be unique (duplicate: "${raw}").`);
          return;
        }
        namesLower.push(lo);
      }
    } else if (effectiveAddKind === "api") {
      const missingTemplateForActive = ordered.find(
        (key) => addDraft[key] === "Active" && !isChosenTemplateId(addDraft.templates[key])
      );
      if (missingTemplateForActive) {
        const label = channels.find((c) => c.key === missingTemplateForActive)?.label || missingTemplateForActive;
        setAddError(`Choose a ${label} template from the list when this channel is enabled.`);
        return;
      }
      if (voiceCallEnabled && voicePromptSelections.length === 0) {
        setAddError("Choose at least one Voice Call template when Voice Call is enabled.");
        return;
      }
    } else {
      const badTemplates = cronFormRows.some((row) => {
        const anyOn = CRON_FLOW_CHANNEL_KEYS.some((k) => row.channelActive[k]);
        if (!anyOn) return true;
        return CRON_FLOW_CHANNEL_KEYS.some(
          (k) => row.channelActive[k] && !isChosenTemplateId(row.channelTemplates[k])
        );
      });
      if (badTemplates) {
        setAddError(
          "Each cron schedule needs at least one channel enabled, and every enabled channel needs a template selected."
        );
        return;
      }
    }

    const effectiveDraft: CommunicationRow =
      effectiveAddKind === "cron"
        ? addKind === "voice"
          ? {
              ...addDraft,
              whatsapp: "Inactive",
              sms: "Inactive",
              rcs: "Inactive",
              email: "Inactive",
              inApp: "Inactive",
              templates: {
                whatsapp: "",
                sms: "",
                rcs: "",
                email: "",
                inApp: "",
              },
            }
          : (() => {
              const mergedTemplates: Templates = {
                whatsapp: "",
                sms: "",
                rcs: "",
                email: "",
                inApp: "",
              };
              const mergedChannel: Record<ChannelKey, ChannelStatus> = {
                whatsapp: "Inactive",
                sms: "Inactive",
                rcs: "Inactive",
                email: "Inactive",
                inApp: "Inactive",
              };
              for (const row of cronFormRows) {
                for (const key of CRON_FLOW_CHANNEL_KEYS) {
                  if (row.channelActive[key] && isChosenTemplateId(row.channelTemplates[key])) {
                    mergedChannel[key] = "Active";
                    if (!mergedTemplates[key]) mergedTemplates[key] = row.channelTemplates[key];
                  }
                }
              }
              return { ...addDraft, ...mergedChannel, templates: mergedTemplates };
            })()
        : addKind === "voice"
          ? {
              ...addDraft,
              whatsapp: "Inactive",
              sms: "Inactive",
              rcs: "Inactive",
              email: "Inactive",
              inApp: "Inactive",
              templates: {
                whatsapp: "",
                sms: "",
                rcs: "",
                email: "",
                inApp: "",
              },
            }
          : addDraft;

    const activeFirst = ordered.filter((key) => effectiveDraft[key] === "Active");
    const inactiveRest = ordered.filter((key) => effectiveDraft[key] !== "Active");
    const priority = [...activeFirst, ...inactiveRest];

    const newRow: CommunicationRow = {
      ...effectiveDraft,
      communication: name,
      priority,
      dayPlans: buildDayPlans(
        {
          whatsapp: effectiveDraft.whatsapp,
          sms: effectiveDraft.sms,
          rcs: effectiveDraft.rcs,
          email: effectiveDraft.email,
          inApp: effectiveDraft.inApp,
        },
        effectiveDraft.templates
      ),
    };

    if (effectiveAddKind === "cron") {
      const badCron = cronFormRows.some((r) => !buildCronFromRow(r));
      if (badCron) {
        setAddError(
          "Fix cron schedules: weekly needs at least one weekday; custom must be exactly 5 space-separated cron fields."
        );
        return;
      }

      if (addKind === "voice") {
        const missingAgg = cronFormRows.some((r) => !(r.customAggregationPipeline ?? "").trim());
        if (missingAgg) {
          setAddError(
            "Voice call (cron): add a MongoDB aggregation pipeline for each schedule rule (open Mongo query and paste JSON)."
          );
          return;
        }
      } else {
        for (const r of cronFormRows) {
          const rawAgg = (r.customAggregationPipeline ?? "").trim();
          if (rawAgg) continue;
          const from = (r.recipientCreatedFrom ?? "").trim();
          const to = (r.recipientCreatedTo ?? "").trim();
          if (from && !CRON_CREATED_DATE_RE.test(from)) {
            setAddError('Created at “From” must be a valid date (YYYY-MM-DD).');
            return;
          }
          if (to && !CRON_CREATED_DATE_RE.test(to)) {
            setAddError('Created at “To” must be a valid date (YYYY-MM-DD).');
            return;
          }
          if (from && to && from > to) {
            setAddError(
              'Created at filter: “From” must be on or before “To” for each schedule using UI filters.'
            );
            return;
          }
        }

        const channelsInUse = CRON_FLOW_CHANNEL_KEYS.filter((ch) =>
          cronFormRows.some((r) => r.channelActive[ch] && isChosenTemplateId(r.channelTemplates[ch]))
        );
        const anyChannelRows = channelsInUse.some((ch) => recipientByChannel[ch].rows.length > 0);
        if (anyChannelRows) {
          const missingRecipients = channelsInUse.filter((ch) => recipientByChannel[ch].rows.length === 0);
          if (missingRecipients.length > 0) {
            const labels = missingRecipients.map((ch) => channels.find((c) => c.key === ch)?.label || ch).join(", ");
            setAddError(
              `Each active channel needs its own portal import after you start. Still missing: ${labels}.`
            );
            return;
          }
        }
      }
    }

    const voiceCallPayload: Record<string, unknown> = {
      enabled: isVoiceSequenceMode ? true : voiceCallEnabled,
      promptIds: isVoiceSequenceMode
        ? voiceSequence
            .filter((s): s is Extract<VoiceSequenceItem, { kind: "prompt" }> => s.kind === "prompt")
            .map((s) => String(s.prompt.value).trim())
        : voicePromptSelections.map((x) => String(x.value).trim()).filter(Boolean),
    };
    if (isVoiceSequenceMode && voiceSequence.length > 0) {
      voiceCallPayload.voiceSteps = voiceSequence.map((s) =>
        s.kind === "prompt"
          ? { type: "prompt", promptId: String(s.prompt.value).trim() }
          : { type: "variable", variableName: s.variableName.trim() }
      );
      voiceCallPayload.ttsList = buildTtsListFromSequence(voiceSequence);
      const pv = buildPromptVariablesFromSequence(voiceSequence);
      if (pv.length > 0) {
        voiceCallPayload.promptVariables = pv;
      }
    }

    const kindPayload: Record<string, unknown> =
      effectiveAddKind === "cron"
        ? (() => {
            const channelsInUse = CRON_FLOW_CHANNEL_KEYS.filter((ch) =>
              cronFormRows.some((r) => r.channelActive[ch] && isChosenTemplateId(r.channelTemplates[ch]))
            );
            const byChannel: Record<string, Record<string, unknown>> = {};
            for (const ch of channelsInUse) {
              const slice = recipientByChannel[ch];
              if (slice.rows.length === 0) continue;
              const cols = buildSuggestedRecipientColumnNamesForChannel(
                cronFormRows,
                channelTemplates as ChannelTemplatesForParams,
                ch
              );
              if (slice.batchId != null && String(slice.batchId).trim() !== "") {
                byChannel[ch] = {
                  channel: ch,
                  columns: cols,
                  batchId: String(slice.batchId),
                  rowCount: slice.rows.length,
                };
              } else {
                byChannel[ch] = { channel: ch, columns: cols, rows: slice.rows };
              }
            }
            return {
              communicationKind: "cron",
              communicationType: addKind === "voice" ? "voice_call" : "messaging_channels",
              cronSchedules: cronFormRows.map((r) => {
                const base = {
                  preset: r.preset,
                  expression: buildCronFromRow(r),
                  time: r.time,
                  weekDays: r.weekDays,
                  monthDay: r.monthDay,
                  hourlyMinute: r.hourlyMinute,
                  cronCustom: r.cronCustom,
                  channelActive: r.channelActive,
                  channelTemplates: r.channelTemplates,
                  channelCollection: r.channelCollection,
                };

                const rawAgg = (r.customAggregationPipeline ?? "").trim();
                if (rawAgg) {
                  return {
                    ...base,
                    filters: {
                      type: "aggregation" as const,
                      pipeline: rawAgg,
                    },
                  };
                }
                const createdFrom = (r.recipientCreatedFrom ?? "").trim();
                const createdTo = (r.recipientCreatedTo ?? "").trim();
                return {
                  ...base,
                  filters: {
                    type: "filters" as const,
                    roles: r.whatsappRoleFilter ?? ["ALL"],
                    gender: r.recipientGenderFilter ?? "ALL",
                    ...(createdFrom ? { createdFrom } : {}),
                    ...(createdTo ? { createdTo } : {}),
                  },
                };
              }),
              ...(Object.keys(byChannel).length > 0 ? { recipientDataByChannel: byChannel } : {}),
              voiceCall: voiceCallPayload,
            };
          })()
        : {
            communicationKind: "api",
            communicationType: addKind === "voice" ? "voice_call" : "messaging_channels",
            voiceCall: voiceCallPayload,
          };

    let cronTemplatesChanged = false;
    try {
      setIsAdding(true);
      setAddError(null);
      const draftId = addDraft.communicationId?.trim();
      cronTemplatesChanged =
        effectiveAddKind === "cron" &&
        Boolean(draftId) &&
        Array.isArray(initialCronTemplateSnapshotRef.current) &&
        (() => {
          const prev = initialCronTemplateSnapshotRef.current as string[][];
          const next = cronFormRows.map((r) =>
            CRON_FLOW_CHANNEL_KEYS.map((ch) => String(r.channelTemplates[ch] ?? "").trim())
          );
          if (prev.length !== next.length) return true;
          for (let i = 0; i < next.length; i++) {
            const p = prev[i] ?? [];
            const n = next[i] ?? [];
            if (p.length !== n.length) return true;
            for (let j = 0; j < n.length; j++) {
              if ((p[j] ?? "") !== (n[j] ?? "")) return true;
            }
          }
          return false;
        })();
      if (cronTemplatesChanged && !cronTemplateChangeAck) {
        setShowCronTemplateWarning(true);
        return;
      }
      const templateParamNamesPayload =
        addDraft?.templateParamNames ?? newRow.templateParamNames ?? emptyTemplateParamNames();
      const savePayload: Record<string, unknown> = {
        ...(newRow as unknown as Record<string, unknown>),
        templateParamNames: templateParamNamesPayload,
        ...kindPayload,
      };
      const whatsAppActive = newRow.whatsapp === "Active";
      if (!whatsAppActive) {
        savePayload.conversational = false;
        savePayload.webhookRoute = "";
      } else if (!newRow.conversational) {
        savePayload.webhookRoute = "";
      }
      if (draftId) {
        await updateCommunicationServerRow(draftId, savePayload as Record<string, any>);
      } else {
        await createCommunicationServerRow(savePayload as Record<string, any>);
      }
      closeAdd();
      await loadRows(page, pageSize, searchQuery);
    } catch (error: any) {
      setAddError(error?.response?.data?.message || error?.message || "Failed to create communication row.");
    } finally {
      setIsAdding(false);
    }
  };

  const getStatusClassName = (status: string) => {
    if (status === "Active") {
      return "bg-emerald-100 text-emerald-700 border-emerald-200";
    }
    if (status === "Inactive") {
      return "bg-rose-100 text-rose-700 border-rose-200";
    }
    if (status === "Paused") {
      return "bg-amber-100 text-amber-700 border-amber-200";
    }
    return "bg-gray-100 text-gray-700 border-gray-200";
  };

  const channels: Array<{ key: ChannelKey; label: string }> = [
    { key: "whatsapp", label: "WhatsApp" },
    { key: "sms", label: "SMS" },
    { key: "rcs", label: "RCS" },
    { key: "email", label: "Email" },
    { key: "inApp", label: "In-App" },
  ];

  /** Cron schedule rows + API add modal: same visible channels (see CRON_UI_CHANNELS_ENABLED). */
  const cronScheduleChannels = CRON_FLOW_CHANNEL_KEYS.map((key) => channels.find((c) => c.key === key)!);

  type TemplateOption = {
    id: string;
    label: string;
    /** Variable names required in recipient row data (from API template.params) */
    params: string[];
    /** Highest `{{n}}` index found in body/header/footer when present (metadata may omit params). */
    placeholderCount: number;
    bodyText: string;
  };
  type TemplateSelectOption = {
    value: string;
    label: string;
  };

  const whatsappRoleOptions: TemplateSelectOption[] = [
    { value: "ALL", label: "All" },
    { value: "ADMIN", label: "ADMIN" },
    { value: "FBDE", label: "FBDE" },
    { value: "HM", label: "HM" },
    { value: "HR", label: "HR" },
    { value: "SHM", label: "SHM" },
    { value: "BDE", label: "BDE" },
    { value: "TPO", label: "TPO" },
    { value: "COMPANY", label: "COMPANY" },
    { value: "FREELANCE_COUNSELLOR", label: "FREELANCE_COUNSELLOR" },
    { value: "AGENCY", label: "AGENCY" },
    { value: "CONSULTANT", label: "CONSULTANT" },
    { value: "MASTER_BDE", label: "MASTER_BDE" },
    { value: "MODERATOR", label: "MODERATOR" },
    { value: "BDE_INTERN", label: "BDE_INTERN" },
    { value: "FREELANCE_BDE", label: "FREELANCE_BDE" },
    { value: "INTERNAL_TEAM", label: "INTERNAL_TEAM" },
  ];

  const genderFilterOptions: TemplateSelectOption[] = [
    { value: "ALL", label: "All" },
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
  ];

  const maritalStatusFilterOptions: TemplateSelectOption[] = [
    { value: "ALL", label: "All" },
    { value: "Single", label: "Single" },
    { value: "Married", label: "Married" },
    { value: "Divorced", label: "Divorced" },
    { value: "Widowed", label: "Widowed" },
  ];

  type ChannelTemplatesState = {
    items: TemplateOption[];
    loading: boolean;
    search: string;
    page: number;
    total: number;
  };

  const [channelTemplates, setChannelTemplates] = useState<Record<ChannelKey, ChannelTemplatesState>>({
    whatsapp: { items: [], loading: false, search: "", page: 1, total: 0 },
    sms: { items: [], loading: false, search: "", page: 1, total: 0 },
    rcs: { items: [], loading: false, search: "", page: 1, total: 0 },
    email: { items: [], loading: false, search: "", page: 1, total: 0 },
    inApp: { items: [], loading: false, search: "", page: 1, total: 0 },
  });

  useEffect(() => {
    if (!isVoiceCronUi) return;
    const tpn = addDraft?.templateParamNames;
    setCronFormRows((prev) =>
      prev.map((row) =>
        syncCronAggregationProjectForRow(
          row,
          channelTemplates as ChannelTemplatesForParams,
          voiceSequenceVariableFieldNames,
          tpn
        )
      )
    );
  }, [channelTemplates, isVoiceCronUi, voiceSequenceVariableFieldNames, addDraft?.templateParamNames]);

  const channelKeyToApiChannel = (key: ChannelKey): string => {
    if (key === "whatsapp") return "WhatsApp";
    if (key === "sms") return "SMS";
    if (key === "rcs") return "RCS";
    if (key === "email") return "Email";
    if (key === "inApp") return "In-App";
    return "In-App";
  };

  const loadChannelTemplates = async (key: ChannelKey, opts?: { search?: string; page?: number }) => {
    setChannelTemplates((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        loading: true,
        search: opts?.search !== undefined ? opts.search : prev[key].search,
        page: opts?.page !== undefined ? opts.page : prev[key].page,
      },
    }));

    const nextSearch = opts?.search ?? channelTemplates[key].search;
    const nextPage = opts?.page ?? channelTemplates[key].page ?? 1;

    try {
      const json = await getCommunicationTemplates({
        page: nextPage,
        pageSize: 20,
        search: nextSearch.trim() || undefined,
        channels: channelKeyToApiChannel(key),
      });

      const payload: any = json;
      let rawItems: any[] = [];
      if (Array.isArray(payload)) {
        rawItems = payload;
      } else if (Array.isArray(payload?.data)) {
        rawItems = payload.data;
      } else if (Array.isArray(payload?.data?.data)) {
        rawItems = payload.data.data;
      } else if (Array.isArray(payload?.items)) {
        rawItems = payload.items;
      } else if (Array.isArray(payload?.data?.items)) {
        rawItems = payload.data.items;
      } else if (Array.isArray(payload?.records)) {
        rawItems = payload.records;
      } else if (Array.isArray(payload?.data?.records)) {
        rawItems = payload.data.records;
      } else if (Array.isArray(payload?.templates)) {
        rawItems = payload.templates;
      } else if (Array.isArray(payload?.data?.templates)) {
        rawItems = payload.data.templates;
      }

      const templateOptions: TemplateOption[] = rawItems.map((item: any, idx: number) => {
        const id = String(item?._id || item?.templateCode || item?.templateKey || item?.key || `TEMPLATE_${idx + 1}`);
        const name =
          item?.name ||
          item?.templateName ||
          item?.displayName ||
          item?.description ||
          item?.subject ||
          id;
        const params = extractParamNamesFromTemplateItem(item);
        const placeholderCount = getPlaceholderCountFromTemplateItem(item);
        const bodyText = getTemplateBodyTextFromItem(item);
        return { id, label: String(name), params, placeholderCount, bodyText };
      });

      let total = templateOptions.length;
      if (!Array.isArray(json) && json) {
        if (typeof (json as any).total === "number") {
          total = (json as any).total;
        } else if (typeof (json as any)?.data?.total === "number") {
          total = (json as any).data.total;
        }
      }

      setChannelTemplates((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          items: templateOptions,
          loading: false,
          search: nextSearch,
          page: nextPage,
          total,
        },
      }));
    } catch (error) {
      setChannelTemplates((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          loading: false,
          items: [],
        },
      }));
    }
  };

  const ensureTemplateOptionForChannel = async (key: ChannelKey, templateId: string) => {
    const tid = String(templateId ?? "").trim();
    if (!isMongoObjectId(tid)) return;
    if (channelTemplates[key].items.some((i) => i.id === tid)) return;
    try {
      const item = await getCommunicationTemplateById(tid);
      const id = String(item?._id || item?.templateCode || item?.templateKey || tid);
      const name =
        item?.name ||
        item?.templateName ||
        item?.displayName ||
        item?.description ||
        item?.subject ||
        id;
      const params = extractParamNamesFromTemplateItem(item);
      const placeholderCount = getPlaceholderCountFromTemplateItem(item);
      const bodyText = getTemplateBodyTextFromItem(item);
      const opt: TemplateOption = { id, label: String(name), params, placeholderCount, bodyText };
      setChannelTemplates((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          items: [opt, ...prev[key].items.filter((x) => x.id !== opt.id)],
        },
      }));
    } catch {
      // leave id fallback in UI when template lookup fails
    }
  };

  const resolveTemplateOptionForChannel = async (
    key: ChannelKey,
    templateId: string
  ): Promise<TemplateOption | null> => {
    const tid = String(templateId ?? "").trim();
    if (!tid) return null;
    const existing = channelTemplates[key].items.find((i) => i.id === tid);
    if (existing) return existing;
    if (!isMongoObjectId(tid)) return null;
    try {
      const item = await getCommunicationTemplateById(tid);
      const id = String(item?._id || item?.templateCode || item?.templateKey || tid);
      const name =
        item?.name ||
        item?.templateName ||
        item?.displayName ||
        item?.description ||
        item?.subject ||
        id;
      const opt: TemplateOption = {
        id,
        label: String(name),
        params: extractParamNamesFromTemplateItem(item),
        placeholderCount: getPlaceholderCountFromTemplateItem(item),
        bodyText: getTemplateBodyTextFromItem(item),
      };
      setChannelTemplates((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          items: [opt, ...prev[key].items.filter((x) => x.id !== opt.id)],
        },
      }));
      return opt;
    } catch {
      return null;
    }
  };

  const warnIfTemplateParamsMissing = async (
    channelKey: ChannelKey,
    channelLabel: string,
    templateId: string
  ) => {
    const sel = await resolveTemplateOptionForChannel(channelKey, templateId);
    if (!sel) return;
    if (sel.params.length > 0) return;
    if (sel.placeholderCount <= 0) return;
    setTemplateParamsMissingModal({
      channelLabel,
      templateLabel: sel.label,
      placeholderCount: sel.placeholderCount,
    });
  };

  const openTemplateParamEditor = async (
    channelKey: AddFlowRecipientChannelKey,
    channelLabel: string,
    templateId: string
  ) => {
    const tid = String(templateId ?? "").trim();
    if (!tid) return;
    const sel = await resolveTemplateOptionForChannel(channelKey, tid);
    if (!sel) return;
    setTemplateParamEditorModal({
      channelKey,
      channelLabel,
      templateId: tid,
      templateLabel: sel.label,
      templateBody: sel.bodyText,
      slotCount: resolveParamSlotCount(sel),
    });
  };

  const loadChannelTemplatesRef = useRef(loadChannelTemplates);
  loadChannelTemplatesRef.current = loadChannelTemplates;

  const loadVoicePromptOptions = async () => {
    if (isVoicePromptsLoading) return;
    setIsVoicePromptsLoading(true);
    try {
      const options = await getObdPromptOptions();
      setVoicePromptOptions(options);
      setVoicePromptSelections((prev) =>
        prev.map((sel) => options.find((opt) => opt.value === sel.value) || sel)
      );
      setVoiceSequence((prev) =>
        prev.map((item) =>
          item.kind === "prompt"
            ? {
                ...item,
                prompt: options.find((opt) => opt.value === item.prompt.value) || item.prompt,
              }
            : item
        )
      );
    } catch (error: any) {
      toast.error(error?.message || "Failed to load voice call templates.");
      setVoicePromptOptions([]);
    } finally {
      setIsVoicePromptsLoading(false);
    }
  };

  const addVoiceVariableToSequence = () => {
    setVoiceSequence((seq) => {
      const nVar = seq.filter((s) => s.kind === "variable").length;
      return [
        ...seq,
        {
          id: newVoiceSequenceItemId(),
          kind: "variable" as const,
          variableName: defaultVoiceApiVariableName(nVar),
        },
      ];
    });
  };

  const debouncedLoadChannelTemplatesSearch = useDebouncedCallback(
    (key: ChannelKey, search: string) => {
      void loadChannelTemplatesRef.current(key, { search, page: 1 });
    },
    TEMPLATE_SEARCH_DEBOUNCE_MS
  );

  const cronChannelsInUse = useMemo(
    () =>
      CRON_FLOW_CHANNEL_KEYS.filter((ch) =>
        cronFormRows.some((r) => r.channelActive[ch] && isChosenTemplateId(r.channelTemplates[ch]))
      ),
    [cronFormRows]
  );

  const suggestedRecipientByChannel = useMemo(() => {
    const tpl = channelTemplates as ChannelTemplatesForParams;
    const overrides = addDraft?.templateParamNames;
    return {
      whatsapp: buildSuggestedRecipientColumnNamesForChannel(cronFormRows, tpl, "whatsapp", overrides),
      sms: buildSuggestedRecipientColumnNamesForChannel(cronFormRows, tpl, "sms", overrides),
      rcs: buildSuggestedRecipientColumnNamesForChannel(cronFormRows, tpl, "rcs", overrides),
      email: buildSuggestedRecipientColumnNamesForChannel(cronFormRows, tpl, "email", overrides),
    };
  }, [cronFormRows, channelTemplates, addDraft?.templateParamNames]);

  useEffect(() => {
    if (!addOpen || effectiveAddKind !== "cron") return;
    const tpl = channelTemplates as ChannelTemplatesForParams;
    const cleared: AddFlowRecipientChannelKey[] = [];
    const overrides = addDraft?.templateParamNames;
    for (const ch of CRON_FLOW_CHANNEL_KEYS) {
      const cols = buildSuggestedRecipientColumnNamesForChannel(cronFormRows, tpl, ch, overrides);
      const key = cols.map((c) => c.toLowerCase()).sort().join("|");
      const prev = lastCronRecipientAutoKeyByChannelRef.current[ch] ?? "";
      if (key === prev) continue;
      lastCronRecipientAutoKeyByChannelRef.current[ch] = key;
      cleared.push(ch);
    }
    if (cleared.length === 0) return;
    setRecipientByChannel((prev) => {
      const next = { ...prev };
      for (const ch of cleared) next[ch] = emptyRecipientChannelSlice();
      return next;
    });
    setRecipientCsvEditorOpenByChannel((prev) => {
      const next = { ...prev };
      for (const ch of cleared) delete next[ch];
      return next;
    });
    setRecipientParseHint(
      "Template columns changed — import again from the portal for each affected channel."
    );
  }, [addOpen, effectiveAddKind, cronFormRows, channelTemplates, addDraft?.templateParamNames]);

  const recipientCronDataInvalid =
    effectiveAddKind === "cron" &&
    !isVoiceCronUi &&
    cronChannelsInUse.some((ch) => recipientByChannel[ch].rows.length > 0) &&
    cronChannelsInUse.some((ch) => recipientByChannel[ch].rows.length === 0);

  const testAggregationForRow = async (row: CronScheduleFormRow) => {
    const raw = (row.customAggregationPipeline ?? "").trim();
    if (!raw) {
      toast.error("Add an aggregation pipeline before testing.");
      return;
    }
    setIsTestingAggregation(true);
    setAggregationTestResult(null);
    setAggregationTestOk(null);
    setAggregationTestCount(null);
    setShowAggregationSample(false);
    try {
      let parsed: unknown;
      try {
        parsed = JSON.parse(raw);
      } catch (e) {
        toast.error("Aggregation pipeline is not valid JSON.");
        return;
      }
      const collection = recipientCollectionToPortalDb(channelCollectionFromRow(row));
      const baseUrl = process.env.REACT_APP_TEMPLATES_API_URL || "";
      const resp = await fetch(`${baseUrl}/communication-server/test-aggregation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          collection,
          pipeline: parsed,
          limit: 5,
        }),
      });
      const json = await resp.json();
      if (!json?.ok) {
        setAggregationTestResult(null);
        toast.error(json?.error || "Aggregation test failed.");
        setAggregationTestOk(false);
        return;
      }
      const results = Array.isArray(json?.results) ? json.results : [];
      setAggregationTestResult(JSON.stringify(results.slice(0, 5), null, 2));
      setAggregationTestOk(true);
      const total =
        typeof json?.total === "number"
          ? json.total
          : typeof json?.count === "number"
          ? json.count
          : results.length;
      setAggregationTestCount(total);
      toast.success(
        `Aggregation test returned ${total} result(s). Showing first ${Math.min(
          results.length,
          5
        )}.`
      );
    } catch (err: any) {
      toast.error(err?.message || "Failed to test aggregation pipeline.");
    } finally {
      setIsTestingAggregation(false);
    }
  };

  const formatAggregationForRow = (row: CronScheduleFormRow) => {
    const raw = row.customAggregationPipeline ?? "";
    if (!raw.trim()) {
      toast.error("Nothing to format – add a pipeline first.");
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      const formatted = JSON.stringify(parsed, null, 2);
      setCronFormRows((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, customAggregationPipeline: formatted } : r))
      );
      toast.success("Aggregation pipeline formatted.");
    } catch {
      toast.error("Cannot format – pipeline is not valid JSON.");
    }
  };

  const voiceCallSequenceSection = (
    <div className="mt-4 rounded-md border border-amber-100 bg-amber-50/60 p-3 relative">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-gray-900">
            {voiceSubKind === "cron" ? "Voice call (cron schedule)" : "Voice call (API triggered)"}
          </p>
          <p className="text-xs text-gray-600">
            {voiceSubKind === "cron"
              ? "Build the voice sequence the same way as API-triggered calls. Recipients and variable values come from the Mongo aggregation in each cron rule below."
              : "Build the voice sequence in order (prompts and variables). No WhatsApp or email templates for this flow."}
          </p>
        </div>
      </div>
      <div className="rounded-md border border-gray-200 bg-white p-3 shadow-sm">
        <div className="rounded-md border border-gray-200 bg-gray-50/80 p-2">
          <div className="rounded-md border border-gray-200 bg-white p-3 shadow-sm">
            <label className="mb-1 block text-xs font-medium text-gray-700">
              Voice sequence (exact order)
            </label>
            <p className="mb-2 text-[10px] text-gray-600">
              Choose a prompt from the dropdown to append a prompt chip, or use &quot;+ Add variable&quot; to append a
              variable chip — order is preserved (e.g. prompt → variable → prompt). Variable chips show{" "}
              <span className="font-mono">v</span>; hover for the payload field name.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <div className="min-w-0 flex-1" key={voicePromptPickerKey}>
                <Select<ObdPromptOption, false>
                  options={voicePromptOptions}
                  value={null}
                  isClearable={false}
                  isSearchable
                  isLoading={isVoicePromptsLoading}
                  placeholder="Pick a prompt to add next…"
                  onMenuOpen={() => {
                    void loadVoicePromptOptions();
                  }}
                  onFocus={() => {
                    if (voicePromptOptions.length === 0) void loadVoicePromptOptions();
                  }}
                  onChange={(opt) => {
                    if (!opt) return;
                    setAddError(null);
                    const full = voicePromptOptions.find((o) => o.value === opt.value) || opt;
                    setVoiceSequence((s) => [
                      ...s,
                      { id: newVoiceSequenceItemId(), kind: "prompt", prompt: full },
                    ]);
                    setVoicePromptPickerKey((k) => k + 1);
                  }}
                  className="w-full text-xs"
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 10000 }),
                  }}
                />
              </div>
              <button
                type="button"
                onClick={addVoiceVariableToSequence}
                className="shrink-0 rounded-md border border-[#EC6E51] bg-white px-3 py-2 text-[11px] font-semibold text-[#c45a3f] hover:bg-orange-50"
              >
                + Add variable
              </button>
            </div>
            <div className="mt-2 min-h-[40px] rounded-md border border-[hsl(0,0%,80%)] bg-white px-1.5 py-1.5">
              {voiceSequence.length === 0 ? (
                <span className="px-1 text-[11px] text-gray-400">
                  No steps yet — add a prompt or variable above.
                </span>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {(() => {
                    let variableStepIndex = 0;
                    return voiceSequence.map((step) => {
                      if (step.kind === "prompt") {
                        return (
                          <span
                            key={step.id}
                            className="inline-flex max-w-[min(100%,280px)] shrink-0 items-center gap-0.5 rounded bg-[hsl(0,0%,90%)] pl-2 pr-0.5 text-[12px] text-gray-800"
                            title={step.prompt.label}
                          >
                            <span className="truncate text-[11px]">{step.prompt.label}</span>
                            <button
                              type="button"
                              onClick={() => setVoiceSequence((s) => s.filter((x) => x.id !== step.id))}
                              className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-gray-500 hover:bg-gray-300/60 hover:text-gray-800"
                              aria-label="Remove prompt"
                            >
                              ×
                            </button>
                          </span>
                        );
                      }
                      const resolvedField =
                        step.variableName.trim() || defaultVoiceApiVariableName(variableStepIndex);
                      variableStepIndex += 1;
                      return (
                        <span
                          key={step.id}
                          className="inline-flex max-w-[min(100%,220px)] shrink-0 items-center gap-0.5 rounded bg-[hsl(0,0%,90%)] pl-2 pr-0.5 text-[12px] text-gray-800"
                          title={`Payload field: ${resolvedField}`}
                        >
                          <span className="truncate font-mono text-[11px] font-semibold">{resolvedField}</span>
                          <button
                            type="button"
                            onClick={() => setVoiceSequence((s) => s.filter((x) => x.id !== step.id))}
                            className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-gray-500 hover:bg-gray-300/60 hover:text-gray-800"
                            aria-label={`Remove variable (${resolvedField})`}
                          >
                            ×
                          </button>
                        </span>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
            {voiceSequence.some((s) => s.kind === "variable") && (
              <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
                <p className="text-[10px] font-medium text-gray-700">Edit variable names</p>
                {voiceSequence.map((step, idx) =>
                  step.kind === "variable" ? (
                    <label
                      key={step.id}
                      className="flex items-center gap-2 text-[10px] text-gray-600"
                    >
                      <span className="w-24 shrink-0 text-gray-500">Seq. #{idx + 1}</span>
                      <input
                        type="text"
                        value={step.variableName}
                        onChange={(e) => {
                          const v = e.target.value;
                          setVoiceSequence((prev) =>
                            prev.map((s, j) =>
                              j === idx && s.kind === "variable" ? { ...s, variableName: v } : s
                            )
                          );
                        }}
                        className="min-w-0 flex-1 rounded border border-gray-300 px-1.5 py-1 text-[11px] text-gray-900 outline-none focus:border-[#EC6E51]"
                        autoComplete="off"
                        spellCheck={false}
                      />
                    </label>
                  ) : null
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderWhatsAppConversationalFields = () => {
    if (!addDraft || addKind === "voice") return null;
    return (
      <div className="mt-3 rounded-md border border-violet-100 bg-violet-50/50 p-2">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div>
            <span className="block text-xs font-medium text-gray-800">Conversational WhatsApp</span>
           
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={addDraft.conversational === true}
            aria-label="Toggle conversational WhatsApp"
            onClick={() =>
              setAddDraft({
                ...addDraft,
                conversational: !addDraft.conversational,
              })
            }
            className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[#EC6E51] focus:ring-offset-1 ${
              addDraft.conversational ? "bg-[#EC6E51]" : "bg-gray-300"
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
                addDraft.conversational ? "translate-x-5" : "translate-x-0"
              }`}
            />
          </button>
        </div>
        {addDraft.conversational && (
          <div className="mt-3">
            <label className="mb-1 block text-xs font-medium text-gray-700">
              Webhook route <span className="text-gray-400">(optional)</span>
            </label>
            <input
              type="text"
              value={addDraft.webhookRoute ?? ""}
              onChange={(e) =>
                setAddDraft({
                  ...addDraft,
                  webhookRoute: e.target.value,
                })
              }
              placeholder="/api/webhook/conversation or https://your-server/webhook"
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-xs outline-none focus:border-[#EC6E51] focus:ring-1 focus:ring-[#EC6E51]"
            />
            <p className="mt-1 text-[11px] text-gray-600">
              If provided, conversation events can be posted to this route and saved with this communication.
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Communication Server</h1>
          <p className="mt-2 text-gray-600">Active Communication</p>
        </div>
        <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
        <button
          onClick={() => navigate("/communication-server/logs")}
          className="rounded-md bg-[#EC6E51] px-4 py-2 text-sm font-semibold text-white hover:bg-[#d85f44]"
        >
          Show Logs
        </button>
        <button
          onClick={() => navigate("/communication-server/templates")}
          className="rounded-md border border-[#EC6E51] px-4 py-2 text-sm font-semibold text-[#EC6E51] hover:bg-[#FFF2ED]"
        >
          Templates
        </button>
          <button
            onClick={openAdd}
            className="rounded-md border border-dashed border-[#EC6E51] px-4 py-2 text-sm font-semibold text-[#EC6E51] hover:bg-[#FFF2ED]"
          >
            + Add
          </button>
        </div>
      </div>
      <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4">
        <label className="mb-1 block text-sm font-medium text-gray-800">Search Communication</label>
        <input
          value={searchQuery}
          onChange={(e) => {
            setPage(1);
            setSearchQuery(e.target.value);
          }}
          placeholder="Search communication name"
          className="w-full rounded-md border border-gray-300 p-2 text-sm outline-none focus:border-[#EC6E51] focus:ring-1 focus:ring-[#EC6E51]"
        />
      </div>

      <div className="mt-6 overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                Communication
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                WhatsApp
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                SMS
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                RCS
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                Email
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                Voice Call
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                Strategy
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                Enabled
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((row, idx) => (
              <React.Fragment key={row.communicationId || `${idx}-${row.communication}`}>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.communication}</td>
                  <td className="px-4 py-3 text-sm">
                    <ActiveChannelStatusChip
                      status={row.whatsapp}
                      channelKey="whatsapp"
                      templateRef={row.templates.whatsapp}
                      statusClassName={getStatusClassName(row.whatsapp)}
                    />
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <ActiveChannelStatusChip
                      status={row.sms}
                      channelKey="sms"
                      templateRef={row.templates.sms}
                      statusClassName={getStatusClassName(row.sms)}
                    />
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <ActiveChannelStatusChip
                      status={row.rcs}
                      channelKey="rcs"
                      templateRef={row.templates.rcs}
                      statusClassName={getStatusClassName(row.rcs)}
                    />
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <ActiveChannelStatusChip
                      status={row.email}
                      channelKey="email"
                      templateRef={row.templates.email}
                      statusClassName={getStatusClassName(row.email)}
                    />
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span
                      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold ${
                        isVoiceCallCommunicationType(row.communicationType)
                          ? "bg-green-100 text-green-700 border-green-200"
                          : "bg-rose-100 text-rose-700 border-rose-200"
                      }`}
                    >
                      {isVoiceCallCommunicationType(row.communicationType) ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-700">
                    {row.deliveryMode === "all" ? "Send All Selected" : "Fallback by Priority"}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={row.enabled}
                      aria-label={row.enabled ? "Turn communication off" : "Turn communication on"}
                      disabled={
                        !row.communicationId || togglingEnabledId === row.communicationId
                      }
                      onClick={() => void handleToggleRowEnabled(idx, !row.enabled)}
                      className={`inline-flex h-5 w-8 shrink-0 items-center rounded-full p-0.5 transition-colors duration-200 focus:outline-none focus-visible:ring-1 focus-visible:ring-[#EC6E51] focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 ${
                        row.enabled ? "bg-[#EC6E51]" : "bg-gray-300"
                      }`}
                    >
                      <span
                        className={`pointer-events-none h-3 w-3 shrink-0 rounded-full bg-white shadow-sm transition-[margin] duration-200 ease-in-out ${
                          row.enabled ? "ml-auto" : "ml-0"
                        }`}
                      />
                      <span className="sr-only">{row.enabled ? "On" : "Off"}</span>
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex flex-wrap items-center gap-2">
                      {row.communicationKind !== "cron" && (
                        <button
                          type="button"
                          onClick={() => void copySampleCurl(idx)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                          aria-label="Copy sample curl"
                          title="Copy sample curl"
                        >
                          <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => openEditor(idx)}
                        className="rounded-md bg-[#EC6E51] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#d85f44]"
                      >
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>
              </React.Fragment>
            ))}
            {!isLoadingRows && rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-500">
                  No communication data found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm text-gray-700">
        <div>
          {total > 0 ? (
            <span>
              Showing{" "}
              <span className="font-semibold">{Math.min((page - 1) * pageSize + 1, total)}</span> to{" "}
              <span className="font-semibold">{Math.min(page * pageSize, total)}</span> of{" "}
              <span className="font-semibold">{total}</span> rows
            </span>
          ) : (
            <span>No rows to display</span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1">
            <span className="text-xs text-gray-600">Rows per page</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPage(1);
                setPageSize(Number(e.target.value) || 10);
              }}
              className="rounded-md border border-gray-300 px-2 py-1 text-xs outline-none focus:border-[#EC6E51] focus:ring-1 focus:ring-[#EC6E51]"
            >
              {[5, 10, 20, 50].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            disabled={page <= 1 || isLoadingRows}
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            className="rounded-md border border-gray-300 px-2 py-1 text-xs font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-60 hover:bg-gray-50"
          >
            Prev
          </button>
          <span className="px-2 text-xs text-gray-600">
            Page <span className="font-semibold">{page}</span> of{" "}
            <span className="font-semibold">{Math.max(1, Math.ceil(total / pageSize))}</span>
          </span>
          <button
            type="button"
            disabled={page >= Math.max(1, Math.ceil(total / pageSize)) || isLoadingRows}
            onClick={() => setPage((prev) => Math.min(Math.max(1, Math.ceil(total / pageSize)), prev + 1))}
            className="rounded-md border border-gray-300 px-2 py-1 text-xs font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-60 hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      </div>

      {addOpen && addDraft && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
          <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
            <div className="shrink-0 border-b border-gray-100 p-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {addDraft.communicationId ? "Edit Communication" : "Add Communication"}
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                API = triggered by your server; Cron = runs on a repeating schedule with optional per-recipient data.
              </p>
            </div>

            <form
              className="min-h-0 flex-1 overflow-y-auto p-4"
              onSubmit={(e) => {
                e.preventDefault();
                void addCommunicationRow();
              }}
            >
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-6">
                <div className="min-w-0 flex-1">
                  <label className="mb-1 block text-sm font-medium text-gray-800">Communication name</label>
                  <input
                    value={addDraft.communication}
                    onChange={(e) => {
                      const nextValue = e.target.value.slice(0, 80);
                      setAddDraft({ ...addDraft, communication: nextValue });
                      if (nameError) {
                        setNameError(validateCommunicationName(nextValue));
                      }
                    }}
                    onBlur={() => {
                      setNameError(validateCommunicationName(addDraft.communication));
                    }}
                    placeholder="e.g. Candidate Onboarding"
                    className="w-full rounded-md border border-gray-300 p-2 text-sm outline-none focus:border-[#EC6E51] focus:ring-1 focus:ring-[#EC6E51]"
                  />
                  {nameError && <p className="mt-1 text-xs text-rose-600">{nameError}</p>}
                  <div className="mt-3">
                    <span className="mb-1 block text-sm font-medium text-gray-800">Recipient collection</span>
                    <p className="mb-2 text-xs text-gray-600">
                      Choose whether this communication uses portal{" "}
                      <span className="font-medium">candidates</span>,{" "}
                      <span className="font-medium">users</span>, or{" "}
                      <span className="font-medium">approaches</span> (API sends, imports, and cron schedule defaults).
                    </p>
                    <div className="rounded-md border border-gray-200 bg-gray-50/90 px-2 py-2">
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                        <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                          <input
                            type="radio"
                            name="add-communication-recipient-collection"
                            className="h-3.5 w-3.5 shrink-0 accent-[#EC6E51]"
                            checked={addDraft.defaultRecipientCollection === "candidate"}
                            onChange={() => setAddModalRecipientCollection("candidate")}
                          />
                          Candidates
                        </label>
                        <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                          <input
                            type="radio"
                            name="add-communication-recipient-collection"
                            className="h-3.5 w-3.5 shrink-0 accent-[#EC6E51]"
                            checked={addDraft.defaultRecipientCollection === "user"}
                            onChange={() => setAddModalRecipientCollection("user")}
                          />
                          Users
                        </label>
                        <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                          <input
                            type="radio"
                            name="add-communication-recipient-collection"
                            className="h-3.5 w-3.5 shrink-0 accent-[#EC6E51]"
                            checked={addDraft.defaultRecipientCollection === "approach"}
                            onChange={() => setAddModalRecipientCollection("approach")}
                          />
                          Approaches
                        </label>
                      </div>
                    </div>
                    <span className="mt-3 mb-1 block text-sm font-medium text-gray-800">Runs on</span>
                    <p className="mb-2 text-xs text-gray-600">
                      API when your backend triggers the communication; Cron for a repeating schedule.
                    </p>
                    <div className="rounded-md border border-gray-200 bg-gray-50/90 px-2 py-2">
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                        <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                          <input
                            type="radio"
                            name="communication-runs-on"
                            className="h-3.5 w-3.5 shrink-0 accent-[#EC6E51]"
                            checked={effectiveAddKind === "api"}
                            disabled={Boolean(addDraft.communicationId?.trim())}
                            onChange={() => {
                              if (addKind === "voice") setVoiceSubKind("api");
                              else setDefaultSubKind("api");
                            }}
                          />
                          API — triggered from your server
                        </label>
                        <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                          <input
                            type="radio"
                            name="communication-runs-on"
                            className="h-3.5 w-3.5 shrink-0 accent-[#EC6E51]"
                            checked={effectiveAddKind === "cron"}
                            disabled={Boolean(addDraft.communicationId?.trim())}
                            onChange={() => {
                              if (addKind === "voice") setVoiceSubKind("cron");
                              else setDefaultSubKind("cron");
                            }}
                          />
                          Cron — repeating schedule
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="w-full shrink-0 lg:w-64">
                  <label className="mb-1 block text-sm font-medium text-gray-800">Communication type</label>
                  <select
                    value={addKind}
                    onChange={(e) => {
                      const v = e.target.value as AddCommunicationKind;
                      setAddKind(v);
                      if (v === "voice") setVoiceSubKind("api");
                    }}
                    disabled={Boolean(addDraft.communicationId?.trim())}
                    className="w-full rounded-md border border-gray-300 bg-white p-2 text-sm outline-none focus:border-[#EC6E51] focus:ring-1 focus:ring-[#EC6E51] disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-600"
                  >
                    <option value="api">All other channels</option>
                    <option value="voice">Voice call</option>
                    {addKind === "cron" && <option value="cron">All other channels</option>}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    {addDraft.communicationId?.trim()
                      ? "Type is fixed for an existing communication."
                      : addKind === "voice"
                        ? "Voice call channel selected. Choose API or cron under Recipient collection (runs on)."
                        : "All non-voice channels selected. Choose API or cron under Recipient collection (runs on)."}
                  </p>
                </div>
              </div>

              {effectiveAddKind === "cron" && (
                <>
                  {isVoiceCronUi && voiceCallSequenceSection}
                  <div className="mt-4 rounded-md border border-amber-100 bg-amber-50/60 p-3 relative">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Schedule rules</p>
                      <p className="text-xs text-gray-600">
                        Add up to {MAX_CRON_SCHEDULES} rules (e.g. weekdays 9:00 + Saturdays 10:00). Uses standard 5-field
                        cron (minute hour day month weekday).
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 space-y-3">
                    {cronFormRows.map((row, idx) => {
                      const built = buildCronFromRow(row);
                      const showFilterDialog = cronFilterRowId === row.id;
                      return (
                        <div
                          key={row.id}
                          className="rounded-md border border-gray-200 bg-white p-3 shadow-sm"
                        >
                          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                              Rule {idx + 1}
                            </span>
                            {cronFormRows.length > 1 && (
                              <button
                                type="button"
                                onClick={() => setCronFormRows((prev) => prev.filter((r) => r.id !== row.id))}
                                className="text-xs font-medium text-rose-600 hover:underline"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-3">
                            <div className="min-w-[140px]">
                              <label className="mb-1 block text-xs text-gray-600">Pattern</label>
                              <select
                                value={row.preset}
                                onChange={(e) =>
                                  setCronFormRows((prev) =>
                                    prev.map((r) =>
                                      r.id === row.id ? { ...r, preset: e.target.value as CronPreset } : r
                                    )
                                  )
                                }
                                className="w-full rounded-md border border-gray-300 p-2 text-sm"
                              >
                                <option value="daily">Daily (time)</option>
                                <option value="weekly">Weekly (weekdays + time)</option>
                                <option value="monthly">Monthly (day of month + time)</option>
                                <option value="hourly">Hourly (at minute)</option>
                                <option value="custom">Custom (5 fields)</option>
                              </select>
                            </div>
                            {(row.preset === "daily" || row.preset === "weekly" || row.preset === "monthly") && (
                              <div className="min-w-[100px]">
                                <label className="mb-1 block text-xs text-gray-600">Time</label>
                                <input
                                  type="time"
                                  value={row.time}
                                  onChange={(e) =>
                                    setCronFormRows((prev) =>
                                      prev.map((r) => (r.id === row.id ? { ...r, time: e.target.value } : r))
                                    )
                                  }
                                  className="w-full rounded-md border border-gray-300 p-2 text-sm"
                                />
                              </div>
                            )}
                            {row.preset === "weekly" && (
                              <div className="min-w-0 flex-1">
                                <label className="mb-1 block text-xs text-gray-600">Weekdays</label>
                                <div className="flex flex-wrap gap-1.5">
                                  {WEEKDAY_OPTIONS.map((d) => (
                                    <label
                                      key={d.v}
                                      className="flex cursor-pointer items-center gap-1 rounded border border-gray-200 bg-gray-50 px-2 py-1 text-xs"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={row.weekDays.includes(d.v)}
                                        onChange={(e) =>
                                          setCronFormRows((prev) =>
                                            prev.map((r) => {
                                              if (r.id !== row.id) return r;
                                              const next = e.target.checked
                                                ? [...r.weekDays, d.v]
                                                : r.weekDays.filter((x) => x !== d.v);
                                              return { ...r, weekDays: next };
                                            })
                                          )
                                        }
                                        className="accent-[#EC6E51]"
                                      />
                                      {d.l}
                                </label>
                                  ))}
                                </div>
                              </div>
                            )}
                            {row.preset === "monthly" && (
                              <div className="w-24">
                                <label className="mb-1 block text-xs text-gray-600">Day (1–31)</label>
                                <input
                                  type="number"
                                  min={1}
                                  max={31}
                                  value={row.monthDay}
                                  onChange={(e) =>
                                    setCronFormRows((prev) =>
                                      prev.map((r) =>
                                        r.id === row.id
                                          ? { ...r, monthDay: Math.min(31, Math.max(1, Number(e.target.value) || 1)) }
                                          : r
                                      )
                                    )
                                  }
                                  className="w-full rounded-md border border-gray-300 p-2 text-sm"
                                />
                              </div>
                            )}
                            {row.preset === "hourly" && (
                              <div className="w-28">
                                <label className="mb-1 block text-xs text-gray-600">Minute (0–59)</label>
                                <input
                                  type="number"
                                  min={0}
                                  max={59}
                                  value={row.hourlyMinute}
                                  onChange={(e) =>
                                    setCronFormRows((prev) =>
                                      prev.map((r) =>
                                        r.id === row.id
                                          ? {
                                              ...r,
                                              hourlyMinute: Math.min(59, Math.max(0, Number(e.target.value) || 0)),
                                            }
                                          : r
                                      )
                                    )
                                  }
                                  className="w-full rounded-md border border-gray-300 p-2 text-sm"
                                />
                              </div>
                            )}
                            {row.preset === "custom" && (
                              <div className="min-w-[200px] flex-1">
                                <label className="mb-1 block text-xs text-gray-600">Cron (min hour dom mon dow)</label>
                                <input
                                  value={row.cronCustom}
                                  onChange={(e) =>
                                    setCronFormRows((prev) =>
                                      prev.map((r) => (r.id === row.id ? { ...r, cronCustom: e.target.value } : r))
                                    )
                                  }
                                  placeholder="0 9 * * 1-5"
                                  className="w-full rounded-md border border-gray-300 p-2 font-mono text-sm"
                                />
                              </div>
                            )}
                          </div>
                          <p className="mt-2 text-xs text-gray-600">
                            <span className="font-medium text-gray-800">Preview:</span>{" "}
                            <code className="rounded bg-gray-100 px-1 py-0.5 text-[11px]">
                              {built || "— invalid —"}
                            </code>
                          </p>
                          <div className="mt-3 border-t border-gray-100 pt-3">
                            <p className="text-xs font-semibold text-gray-800">
                              {isVoiceCronUi
                                ? "Recipients for this schedule (voice)"
                                : "Channels & templates for this schedule"}
                            </p>
                            <p className="mb-1 text-xs text-gray-500">
                              {isVoiceCronUi
                                ? "Use Mongo query below — no WhatsApp or email templates for voice cron."
                                : "Pick templates here per schedule. Each run uses only this row's selection."}
                            </p>
                            <div className="mb-2 flex flex-col gap-2 text-xs">
                              {(() => {
                                const anyChannelOn = CRON_FLOW_CHANNEL_KEYS.some(
                                  (ch) => row.channelActive[ch]
                                );
                                const recipientQueryEnabled = isVoiceCronUi || anyChannelOn;
                                const isMongoQueryMode =
                                  (row.customAggregationPipeline ?? "").trim().length > 0;
                                const selectedBtn =
                                  "border-[#EC6E51] bg-[#FFF5F2] text-[#5c2e24] shadow-sm ring-2 ring-[#EC6E51]/40";
                                const idleBtn =
                                  "border-gray-300 bg-white text-gray-700 hover:bg-gray-50";
                                return (
                                  <>
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                      <span className="text-[11px] font-medium text-gray-800">
                                        Recipient query
                                      </span>
                                      <span className="text-[10px] text-gray-500">
                                        {isVoiceCronUi
                                          ? "Voice cron uses MongoDB aggregation only."
                                          : isMongoQueryMode
                                            ? "Using custom Mongo aggregation for this rule."
                                            : "Using UI filters (collection & roles) for this rule."}
                                      </span>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                      {!isVoiceCronUi && (
                                      <button
                                        type="button"
                                        disabled={!recipientQueryEnabled}
                                        onClick={() => {
                                          if (!recipientQueryEnabled) return;
                                          setCronMongoRowId(null);
                                          setCronFormRows((prev) =>
                                            prev.map((r) =>
                                              r.id === row.id
                                                ? { ...r, customAggregationPipeline: "" }
                                                : r
                                            )
                                          );
                                          setCronFilterRowId(row.id);
                                        }}
                                        className={`inline-flex min-h-[2rem] flex-1 items-center justify-center gap-1.5 rounded-md border px-2.5 py-1.5 text-[11px] font-medium transition-colors sm:flex-initial sm:justify-start ${
                                          !isMongoQueryMode ? selectedBtn : idleBtn
                                        } ${!recipientQueryEnabled ? "cursor-not-allowed opacity-60" : ""}`}
                                        aria-pressed={!isMongoQueryMode}
                                        aria-label="Use UI filters for recipients"
                                      >
                                        {!isMongoQueryMode ? (
                                          <Check className="h-3.5 w-3.5 shrink-0 text-[#C45A40]" aria-hidden />
                                        ) : null}
                                        <svg
                                          className="h-3.5 w-3.5 shrink-0 text-current"
                                          viewBox="0 0 20 20"
                                          fill="currentColor"
                                          aria-hidden="true"
                                        >
                                          <path d="M3 4.75A.75.75 0 0 1 3.75 4h12.5a.75.75 0 0 1 .53 1.28L12 10.06v5.19a.75.75 0 0 1-1.13.64l-2.5-1.5A.75.75 0 0 1 8 13.75v-3.69L3.22 5.28A.75.75 0 0 1 3 4.75Z" />
                                        </svg>
                                        <span>UI filters</span>
                                        {!isMongoQueryMode ? (
                                          <span className="ml-0.5 rounded bg-[#EC6E51]/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-[#9A3F2E]">
                                            Selected
                                          </span>
                                        ) : null}
                                      </button>
                                      )}
                                      <button
                                        type="button"
                                        disabled={!recipientQueryEnabled}
                                        onClick={() => {
                                          if (!recipientQueryEnabled) return;
                                          setCronFilterRowId(null);
                                          setCronFormRows((prev) =>
                                            prev.map((r) => {
                                              if (r.id !== row.id) return r;
                                              const existing = (r.customAggregationPipeline ?? "").trim();
                                              if (existing) return r;
                                              const pipeline: Record<string, unknown>[] = [
                                                {
                                                  $match: cronStatusMatchForCollection(
                                                    channelCollectionFromRow(r),
                                                  ),
                                                },
                                              ];
                                              let paramNames = collectParamNamesForCronScheduleRow(
                                                r,
                                                channelTemplates as ChannelTemplatesForParams,
                                                addDraft?.templateParamNames
                                              );
                                              if (isVoiceCronUi) {
                                                paramNames = mergeCronProjectFieldNames(
                                                  paramNames,
                                                  voiceSequenceVariableFieldNames
                                                );
                                              }
                                              if (paramNames.length > 0) {
                                                const project =
                                                  isVoiceCronUi
                                                    ? buildVoiceCronProjectStage(paramNames)
                                                    : (() => {
                                                        const base: Record<string, 1> = { _id: 1 };
                                                        for (const k of paramNames) {
                                                          base[k] = 1;
                                                        }
                                                        return base;
                                                      })();
                                                pipeline.push({ $project: project });
                                              }
                                              const prefiller = JSON.stringify(pipeline, null, 2);
                                              return { ...r, customAggregationPipeline: prefiller };
                                            })
                                          );
                                          setCronMongoRowId(row.id);
                                        }}
                                        className={`inline-flex min-h-[2rem] flex-1 items-center justify-center gap-1.5 rounded-md border px-2.5 py-1.5 font-mono text-[11px] font-medium transition-colors sm:flex-initial sm:justify-start ${
                                          isMongoQueryMode ? selectedBtn : idleBtn
                                        } ${!recipientQueryEnabled ? "cursor-not-allowed opacity-60" : ""}`}
                                        aria-pressed={isMongoQueryMode}
                                        aria-label="Use custom MongoDB aggregation for recipients"
                                      >
                                        {isMongoQueryMode ? (
                                          <Check className="h-3.5 w-3.5 shrink-0 text-[#C45A40]" aria-hidden />
                                        ) : null}
                                        <span className="font-mono text-[12px]">{`{}`}</span>
                                        <span className="font-sans">Mongo query</span>
                                        {isMongoQueryMode ? (
                                          <span className="ml-0.5 rounded bg-[#EC6E51]/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-[#9A3F2E]">
                                            Selected
                                          </span>
                                        ) : null}
                                      </button>
                                    </div>
                                  </>
                                );
                              })()}
                            </div>
                            {!isVoiceCronUi && (
                            <div className="grid grid-cols-1 gap-3">
                              {cronScheduleChannels.map((channel) => (
                                <div
                                  key={`${row.id}-${channel.key}`}
                                  className="rounded-md border border-gray-200 bg-gray-50/80 p-2"
                                >
                                  <label className="mb-1 block text-xs font-medium text-gray-700">
                                    {channel.label}
                                  </label>
                                  <div className="flex items-center gap-1">
                                    <Select<TemplateSelectOption, false>
                                      options={channelTemplates[channel.key].items.map((opt) => ({
                                        value: opt.id,
                                        label: opt.label,
                                      }))}
                                      value={(() => {
                                        const selectedId = row.channelTemplates[channel.key];
                                        const selected = channelTemplates[channel.key].items.find(
                                          (opt) => opt.id === selectedId
                                        );
                                        if (selected) return { value: selected.id, label: selected.label };
                                        return selectedId ? { value: selectedId, label: selectedId } : null;
                                      })()}
                                      isSearchable
                                      isClearable={false}
                                      placeholder="Template"
                                      onMenuOpen={() =>
                                        loadChannelTemplates(channel.key, {
                                          search: channelTemplates[channel.key].search,
                                          page: 1,
                                        })
                                      }
                                      onInputChange={(value, meta) => {
                                        if (meta.action === "input-change") {
                                          setChannelTemplates((prev) => ({
                                            ...prev,
                                            [channel.key]: { ...prev[channel.key], search: value },
                                          }));
                                          debouncedLoadChannelTemplatesSearch(channel.key, value);
                                        }
                                        return value;
                                      }}
                                      onChange={async (val) => {
                                        const templateId = val?.value || "";
                                        const sel = await resolveTemplateOptionForChannel(channel.key, templateId);
                                        void warnIfTemplateParamsMissing(channel.key, channel.label, templateId);
                                        const mergedTpn: TemplateParamNamesMap = {
                                          ...(addDraft?.templateParamNames ?? emptyTemplateParamNames()),
                                          [channel.key]: buildTemplateParamNamesForChannel(
                                            addDraft?.templateParamNames?.[channel.key],
                                            sel ?? undefined
                                          ),
                                        };
                                        setAddDraft((d) =>
                                          d ? { ...d, templateParamNames: mergedTpn } : null
                                        );
                                        setCronFormRows((prev) =>
                                          prev.map((r) =>
                                            r.id === row.id
                                              ? (() => {
                                                  const nextRow: CronScheduleFormRow = {
                                                    ...r,
                                                    channelTemplates: {
                                                      ...r.channelTemplates,
                                                      [channel.key]: templateId,
                                                    },
                                                  };
                                                  return syncCronAggregationProjectForRow(
                                                    nextRow,
                                                    channelTemplates as ChannelTemplatesForParams,
                                                    [],
                                                    mergedTpn
                                                  );
                                                })()
                                              : r
                                          )
                                        );
                                      }}
                                      className="w-full text-xs"
                                      menuPortalTarget={document.body}
                                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 10000 }) }}
                                    />
                                    {isChosenTemplateId(row.channelTemplates[channel.key]) && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          if (!isAddFlowRecipientChannelKey(channel.key)) return;
                                          void openTemplateParamEditor(
                                            channel.key,
                                            channel.label,
                                            row.channelTemplates[channel.key]
                                          );
                                        }}
                                        title="Edit param columns"
                                        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[#EC6E51]/40 bg-[#FFF5F2] text-[#9A3F2E] hover:bg-[#FDEAE5]"
                                      >
                                        <Wand2 className="h-3.5 w-3.5" />
                                      </button>
                                    )}
                                  </div>
                                  {isChosenTemplateId(row.channelTemplates[channel.key]) &&
                                    (() => {
                                      const sel = channelTemplates[channel.key].items.find(
                                        (i) => i.id === row.channelTemplates[channel.key]
                                      );
                                      const fallbackCol =
                                        channel.key === "email"
                                          ? "email"
                                          : channel.key === "whatsapp" ||
                                              channel.key === "sms" ||
                                              channel.key === "rcs"
                                            ? "mobile"
                                            : "";
                                      const slotCount = resolveParamSlotCount(sel);
                                      const configuredParamCount = padTemplateParamNames(
                                        addDraft?.templateParamNames?.[channel.key],
                                        slotCount
                                      ).filter((x) => String(x ?? "").trim().length > 0).length;
                                      const hasConfiguredAllParams = slotCount > 0 && configuredParamCount >= slotCount;
                                      return (
                                        <>
                                          <p className="mt-1 text-[10px] leading-snug text-gray-600">
                                            {sel?.params?.length ? (
                                              <>
                                                This template expects CSV columns:{" "}
                                                <code className="rounded bg-white px-0.5">{sel.params.join(", ")}</code>
                                              </>
                                            ) : slotCount > 0 && !hasConfiguredAllParams ? (
                                              <span className="text-amber-900">
                                                Body uses <strong>{slotCount}</strong> placeholder
                                                {slotCount === 1 ? "" : "s"} (
                                                <code className="rounded bg-white px-0.5">{`{{1}}`}</code>
                                                {slotCount > 1 ? (
                                                  <>
                                                    {" "}
                                                    …{" "}
                                                    <code className="rounded bg-white px-0.5">{`{{${slotCount}}}`}</code>
                                                  </>
                                                ) : null}
                                                ) but <code className="text-[10px]">params</code> are missing on this
                                                template. Add param names in Communication Automation or set column names
                                                below.
                                              </span>
                                            ) : hasConfiguredAllParams ? (
                                              <>Using configured column names for all template placeholders.</>
                                            ) : (
                                              <>
                                                No <code className="text-[10px]">params</code> on template — use{" "}
                                                <code className="rounded bg-white px-0.5">{fallbackCol || "—"}</code> in
                                                recipient data if needed
                                              </>
                                            )}
                                          </p>
                                        </>
                                      );
                                    })()}
                                  <label className="mt-2 flex cursor-pointer items-center gap-2 text-xs text-gray-700">
                                    <input
                                      type="checkbox"
                                      checked={row.channelActive[channel.key]}
                                      onChange={(e) => {
                                        const checked = e.target.checked;
                                        if (checked && !isChosenTemplateId(row.channelTemplates[channel.key])) {
                                          setAddError(
                                            `Choose a ${channel.label} template before enabling it on this schedule.`
                                          );
                                          return;
                                        }
                                        setAddError(null);
                                        setCronFormRows((prev) =>
                                          prev.map((r) =>
                                            r.id === row.id
                                              ? {
                                                  ...r,
                                                  channelActive: { ...r.channelActive, [channel.key]: checked },
                                                }
                                              : r
                                          )
                                        );
                                        if (channel.key === "whatsapp" && !checked) {
                                          setAddDraft((d) =>
                                            d ? { ...d, conversational: false } : null,
                                          );
                                        }
                                      }}
                                      className="h-3.5 w-3.5 rounded border-gray-300 accent-[#EC6E51]"
                                    />
                                    Send on this channel when this schedule runs
                                  </label>
                                  {channel.key === "whatsapp" &&
                                    isChosenTemplateId(row.channelTemplates.whatsapp) &&
                                    row.id === cronFormRows[0]?.id &&
                                    renderWhatsAppConversationalFields()}
                                </div>
                              ))}
                            </div>
                            )}
                            {isVoiceCronUi && (
                              <p className="mt-2 text-[11px] leading-relaxed text-gray-600">
                                Messaging channel templates are hidden for voice cron. Use{" "}
                                <span className="font-medium">Mongo query</span> to define recipients; variable names in
                                the voice sequence should match fields returned by your pipeline (e.g. phone, name).
                              </p>
                            )}
                            {showFilterDialog && (
                              <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4">
                                <div className="w-full max-w-sm rounded-lg bg-white p-4 shadow-lg">
                                  <div className="mb-3 flex items-center justify-between">
                                    <h3 className="text-sm font-semibold text-gray-900">
                                      Channel collection &amp; filter
                                    </h3>
                                    <button
                                      type="button"
                                      onClick={() => setCronFilterRowId(null)}
                                      className="rounded-md border border-gray-300 px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-50"
                                    >
                                      Close
                                    </button>
                                  </div>
                                  <div className="space-y-3 text-xs text-gray-700">
                                    <div>
                                      <p className="mb-1 font-medium text-gray-800">
                                        {isVoiceCronUi ? "Source collection" : "Collection for all channels"}
                                      </p>
                                      <div className="flex flex-wrap gap-4">
                                        <label className="inline-flex items-center gap-1">
                                          <input
                                            type="radio"
                                            name={`cron-${row.id}-collection`}
                                            value="candidate"
                                            checked={isUniformChannelCollection(row, "candidate", ADD_FLOW_CHANNEL_KEYS)}
                                            onChange={() =>
                                              setCronFormRows((prev) =>
                                                prev.map((r) => {
                                                  if (r.id !== row.id) return r;
                                                  const next = { ...r.channelCollection };
                                                  ADD_FLOW_CHANNEL_KEYS.forEach((k) => {
                                                    next[k] = "candidate";
                                                  });
                                                  return {
                                                    ...r,
                                                    channelCollection: next,
                                                    whatsappRoleFilter: ["ALL"],
                                                  };
                                                })
                                              )
                                            }
                                            className="h-3.5 w-3.5 accent-[#EC6E51]"
                                          />
                                          <span>Candidate</span>
                                        </label>
                                        <label className="inline-flex items-center gap-1">
                                          <input
                                            type="radio"
                                            name={`cron-${row.id}-collection`}
                                            value="user"
                                            checked={isUniformChannelCollection(row, "user", ADD_FLOW_CHANNEL_KEYS)}
                                            onChange={() =>
                                              setCronFormRows((prev) =>
                                                prev.map((r) => {
                                                  if (r.id !== row.id) return r;
                                                  const next = { ...r.channelCollection };
                                                  ADD_FLOW_CHANNEL_KEYS.forEach((k) => {
                                                    next[k] = "user";
                                                  });
                                                  return {
                                                    ...r,
                                                    channelCollection: next,
                                                    whatsappRoleFilter: ["ALL"],
                                                  };
                                                })
                                              )
                                            }
                                            className="h-3.5 w-3.5 accent-[#EC6E51]"
                                          />
                                          <span>User</span>
                                        </label>
                                        <label className="inline-flex items-center gap-1">
                                          <input
                                            type="radio"
                                            name={`cron-${row.id}-collection`}
                                            value="approach"
                                            checked={isUniformChannelCollection(row, "approach", ADD_FLOW_CHANNEL_KEYS)}
                                            onChange={() =>
                                              setCronFormRows((prev) =>
                                                prev.map((r) => {
                                                  if (r.id !== row.id) return r;
                                                  const next = { ...r.channelCollection };
                                                  ADD_FLOW_CHANNEL_KEYS.forEach((k) => {
                                                    next[k] = "approach";
                                                  });
                                                  return {
                                                    ...r,
                                                    channelCollection: next,
                                                    whatsappRoleFilter: ["ALL"],
                                                  };
                                                })
                                              )
                                            }
                                            className="h-3.5 w-3.5 accent-[#EC6E51]"
                                          />
                                          <span>Approach</span>
                                        </label>
                                      </div>
                                    </div>
                                    {(() => {
                                      const cronFilterIsUserCollection =
                                        isUniformChannelCollection(row, "user", ADD_FLOW_CHANNEL_KEYS);
                                      return cronFilterIsUserCollection ? (
                                    <div>
                                      <p className="mb-1 font-medium text-gray-800">Role filter</p>
                                      <Select<TemplateSelectOption, true>
                                        isMulti
                                        closeMenuOnSelect={false}
                                        options={whatsappRoleOptions}
                                        value={whatsappRoleOptions.filter((opt) =>
                                          (row.whatsappRoleFilter ?? []).includes(opt.value)
                                        )}
                                        onChange={(vals) =>
                                          setCronFormRows((prev) =>
                                            prev.map((r) =>
                                              r.id === row.id
                                                ? {
                                                    ...r,
                                                    whatsappRoleFilter: vals.map((v) => v.value),
                                                  }
                                                : r
                                            )
                                          )
                                        }
                                        className="text-xs"
                                        classNamePrefix="wa-filter-select"
                                        menuPortalTarget={document.body}
                                        styles={{
                                          menuPortal: (base) => ({ ...base, zIndex: 11000 }),
                                        }}
                                        placeholder="Select roles"
                                      />
                                    </div>
                                      ) : null;
                                    })()}
                                    <div>
                                      <p className="mb-1 font-medium text-gray-800">Gender filter</p>
                                      <Select<TemplateSelectOption, false>
                                        isMulti={false}
                                        options={genderFilterOptions}
                                        value={
                                          genderFilterOptions.find(
                                            (opt) => opt.value === (row.recipientGenderFilter ?? "ALL")
                                          ) ?? genderFilterOptions[0]
                                        }
                                        onChange={(opt) =>
                                          setCronFormRows((prev) =>
                                            prev.map((r) =>
                                              r.id === row.id
                                                ? {
                                                    ...r,
                                                    recipientGenderFilter: (opt?.value as
                                                      | "ALL"
                                                      | "Male"
                                                      | "Female") ?? "ALL",
                                                  }
                                                : r
                                            )
                                          )
                                        }
                                        className="text-xs"
                                        classNamePrefix="wa-filter-select"
                                        menuPortalTarget={document.body}
                                        styles={{
                                          menuPortal: (base) => ({ ...base, zIndex: 11000 }),
                                        }}
                                        placeholder="Gender"
                                      />
                                    </div>
                                    <div>
                                      <p className="mb-1 font-medium text-gray-800">Created at</p>
                                      <p className="mb-2 text-[11px] text-gray-500">
                                        Optional range on <span className="font-mono">createdAt</span> using each
                                        date as a full calendar day in{" "}
                                        <span className="font-medium">IST (Asia/Kolkata)</span>. Leave blank for no
                                        bound.
                                      </p>
                                      <div className="flex flex-wrap items-end gap-2">
                                        <div className="min-w-[140px] flex-1">
                                          <label className="mb-0.5 block text-[11px] font-medium text-gray-600">
                                            From
                                          </label>
                                          <input
                                            type="date"
                                            value={row.recipientCreatedFrom ?? ""}
                                            onChange={(e) =>
                                              setCronFormRows((prev) =>
                                                prev.map((rr) =>
                                                  rr.id === row.id
                                                    ? { ...rr, recipientCreatedFrom: e.target.value }
                                                    : rr
                                                )
                                              )
                                            }
                                            className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-xs outline-none focus:border-[#EC6E51] focus:ring-1 focus:ring-[#EC6E51]"
                                          />
                                        </div>
                                        <div className="min-w-[140px] flex-1">
                                          <label className="mb-0.5 block text-[11px] font-medium text-gray-600">
                                            To
                                          </label>
                                          <input
                                            type="date"
                                            value={row.recipientCreatedTo ?? ""}
                                            onChange={(e) =>
                                              setCronFormRows((prev) =>
                                                prev.map((rr) =>
                                                  rr.id === row.id
                                                    ? { ...rr, recipientCreatedTo: e.target.value }
                                                    : rr
                                                )
                                              )
                                            }
                                            className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-xs outline-none focus:border-[#EC6E51] focus:ring-1 focus:ring-[#EC6E51]"
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                            {cronMongoRowId === row.id && (
                              <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4">
                                <div className="w-full max-w-sm rounded-lg bg-white p-4 shadow-lg">
                                  <div className="mb-3 flex items-center justify-between">
                                    <h3 className="text-sm font-semibold text-gray-900">
                                      Custom MongoDB aggregation pipeline
                                    </h3>
                                  </div>
                                  <div className="space-y-3 text-xs text-gray-700">
                                    <div>
                                      <p className="mb-1 font-medium text-gray-800">
                                        {isVoiceCronUi ? "Source collection" : "Collection for all channels"}
                                      </p>
                                      <div className="flex flex-wrap gap-4">
                                        <label className="inline-flex items-center gap-1">
                                          <input
                                            type="radio"
                                            name={`cron-${row.id}-mongo-collection`}
                                            value="candidate"
                                            checked={isUniformChannelCollection(row, "candidate", ADD_FLOW_CHANNEL_KEYS)}
                                            onChange={() =>
                                              setCronFormRows((prev) =>
                                                prev.map((r) => {
                                                  if (r.id !== row.id) return r;
                                                  const next = { ...r.channelCollection };
                                                  ADD_FLOW_CHANNEL_KEYS.forEach((k) => {
                                                    next[k] = "candidate";
                                                  });
                                                  return { ...r, channelCollection: next };
                                                })
                                              )
                                            }
                                            className="h-3.5 w-3.5 accent-[#EC6E51]"
                                          />
                                          <span>Candidate</span>
                                        </label>
                                        <label className="inline-flex items-center gap-1">
                                          <input
                                            type="radio"
                                            name={`cron-${row.id}-mongo-collection`}
                                            value="user"
                                            checked={isUniformChannelCollection(row, "user", ADD_FLOW_CHANNEL_KEYS)}
                                            onChange={() =>
                                              setCronFormRows((prev) =>
                                                prev.map((r) => {
                                                  if (r.id !== row.id) return r;
                                                  const next = { ...r.channelCollection };
                                                  ADD_FLOW_CHANNEL_KEYS.forEach((k) => {
                                                    next[k] = "user";
                                                  });
                                                  return { ...r, channelCollection: next };
                                                })
                                              )
                                            }
                                            className="h-3.5 w-3.5 accent-[#EC6E51]"
                                          />
                                          <span>User</span>
                                        </label>
                                        <label className="inline-flex items-center gap-1">
                                          <input
                                            type="radio"
                                            name={`cron-${row.id}-mongo-collection`}
                                            value="approach"
                                            checked={isUniformChannelCollection(row, "approach", ADD_FLOW_CHANNEL_KEYS)}
                                            onChange={() =>
                                              setCronFormRows((prev) =>
                                                prev.map((r) => {
                                                  if (r.id !== row.id) return r;
                                                  const next = { ...r.channelCollection };
                                                  ADD_FLOW_CHANNEL_KEYS.forEach((k) => {
                                                    next[k] = "approach";
                                                  });
                                                  return { ...r, channelCollection: next };
                                                })
                                              )
                                            }
                                            className="h-3.5 w-3.5 accent-[#EC6E51]"
                                          />
                                          <span>Approach</span>
                                        </label>
                                      </div>
                                    </div>
                                    <div>
                                      <p className="mb-1 font-medium text-gray-800">MongoDB aggregation pipeline</p>
                                      <p className="mb-1 text-[11px] text-gray-500">
                                        JSON array of pipeline stages to fetch recipients for this schedule. Leave empty
                                        to use the default model.
                                      </p>
                                      <div className="h-48 w-full overflow-hidden rounded-md border border-gray-300">
                                        <Editor
                                          height="100%"
                                          defaultLanguage="json"
                                          value={row.customAggregationPipeline ?? ""}
                                          // 'value' can be string or undefined; we normalize to string.
                                          onChange={(value: string | undefined) =>
                                            setCronFormRows((prev) =>
                                              prev.map((r) =>
                                                r.id === row.id
                                                  ? { ...r, customAggregationPipeline: value ?? "" }
                                                  : r
                                              )
                                            )
                                          }
                                          options={{
                                            fontSize: 11,
                                            minimap: { enabled: false },
                                            scrollBeyondLastLine: false,
                                            automaticLayout: true,
                                            wordWrap: "on",
                                          }}
                                        />
                                      </div>
                                      <div className="mt-2 flex flex-col gap-1">
                                        <div className="flex justify-end gap-1">
                                          <button
                                            type="button"
                                            onClick={() => formatAggregationForRow(row)}
                                            title="Format JSON"
                                            className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-gray-300 bg-white text-[11px] text-gray-700 hover:bg-gray-50"
                                          >
                                            <Wand2 className="h-3.5 w-3.5" aria-hidden="true" />
                                          </button>
                                          <button
                                            type="button"
                                            disabled={isTestingAggregation}
                                            onClick={() => testAggregationForRow(row)}
                                            title="Test aggregation (first 5)"
                                            className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-gray-300 bg-white text-[11px] text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                                          >
                                            {isTestingAggregation ? (
                                              <span className="h-3 w-3 animate-spin rounded-full border border-gray-400 border-t-transparent" />
                                            ) : (
                                              <Play className="h-3.5 w-3.5" aria-hidden="true" />
                                            )}
                                          </button>
                                        </div>
                                        {aggregationTestOk === true && (
                                          <div className="flex items-center justify-between text-[11px] text-emerald-600">
                                            <span>
                                              Test succeeded
                                              {typeof aggregationTestCount === "number"
                                                ? ` (${aggregationTestCount} result(s), first 5 shown).`
                                                : "."}
                                            </span>
                                            {aggregationTestResult && (
                                              <button
                                                type="button"
                                                onClick={() => setShowAggregationSample((prev) => !prev)}
                                                className="ml-2 text-[11px] font-medium text-emerald-700 hover:underline"
                                              >
                                                {showAggregationSample ? "Hide sample" : "View sample"}
                                              </button>
                                            )}
                                          </div>
                                        )}
                                        {aggregationTestOk === false && (
                                          <p className="text-[11px] text-rose-600">
                                            Last aggregation test failed. Check the pipeline and try again.
                                          </p>
                                        )}
                                        {showAggregationSample && aggregationTestResult && (
                                          <pre className="max-h-40 overflow-auto rounded-md bg-gray-900 p-2 text-[11px] text-gray-100">
{aggregationTestResult}
                                          </pre>
                                        )}
                                      </div>
                                    </div>
                                    <div className="mt-3 flex items-center justify-end gap-2">
                                      <button
                                        type="button"
                                        onClick={() => setCronMongoRowId(null)}
                                        className="rounded-md border border-gray-300 px-3 py-1 text-xs text-gray-700 hover:bg-gray-50"
                                      >
                                        Close
                                      </button>
                                      <button
                                        type="button"
                                        disabled={!aggregationTestOk}
                                        onClick={() => {
                                          if (!aggregationTestOk) return;
                                          setCronMongoRowId(null);
                                          toast.success("Aggregation query attached to this schedule.");
                                        }}
                                        className="rounded-md border border-emerald-500 px-3 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
                                      >
                                        Submit
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                      </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                </>
              )}

              {effectiveAddKind === "cron" && cronChannelsInUse.length > 0 && (
                <div className="mt-4 rounded-md border border-gray-200 bg-slate-50/50 p-3 hidden">
                  <div className="mt-3 space-y-4">
                      {cronChannelsInUse.map((ch) => {
                        const cols = suggestedRecipientByChannel[ch];
                        const slice = recipientByChannel[ch];
                        const label = channels.find((c) => c.key === ch)?.label ?? ch;
                        const csvEditorOpen =
                          slice.rows.length === 0 || recipientCsvEditorOpenByChannel[ch] === true;
                        return (
                          <div
                            key={ch}
                            className="rounded-md border border-amber-200 bg-amber-50/90 px-2 py-2 text-xs text-amber-950"
                          >
                            <p className="font-semibold">{label} recipients</p>
                            <p className="mt-1 text-[10px] text-amber-900/90">
                              Expected columns for <strong>{label}</strong> only (union of{" "}
                              <code className="rounded bg-white/80 px-0.5 text-[10px]">params</code> on that channel,
                              plus <code className="rounded bg-white/80 px-0.5 text-[10px]">recipient_email</code> /{" "}
                              <code className="rounded bg-white/80 px-0.5 text-[10px]">recipient_phone</code>):
                            </p>
                            <div className="mt-1 flex flex-wrap gap-1">
                              {cols.map((c) => (
                                <span
                                  key={c}
                                  className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-amber-950 ring-1 ring-amber-200/80"
                                >
                                  {c}
                                </span>
                              ))}
                            </div>
                            {!csvEditorOpen && slice.rows.length > 0 ? (
                              <div className="mt-2">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <span className="text-[11px] font-medium text-amber-900/95">
                                    {slice.rows.length} row(s) applied — review below or re-import.
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setRecipientCsvEditorOpenByChannel((p) => ({ ...p, [ch]: true }))
                                    }
                                    className="rounded-md border border-amber-400 bg-white px-2.5 py-1 text-[11px] font-semibold text-amber-950 hover:bg-amber-100/80"
                                  >
                                    Re-import
                                  </button>
                                </div>
                                <div className="mt-2 max-h-56 overflow-auto rounded border border-amber-200/60 bg-white">
                                  <table className="min-w-full text-left text-[11px]">
                                    <thead className="sticky top-0 bg-amber-50/90">
                                      <tr>
                                        {cols.map((c) => (
                                          <th key={c} className="px-2 py-1 font-semibold text-gray-700">
                                            {c}
                                          </th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {slice.rows.map((r, i) => (
                                        <tr key={i} className="border-t border-gray-100">
                                          {cols.map((c) => (
                                            <td
                                              key={c}
                                              className="max-w-[160px] truncate px-2 py-1 text-gray-800"
                                              title={r[c]}
                                            >
                                              {r[c]}
                                            </td>
                                          ))}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            ) : (
                              <>
                                <p className="mb-1 mt-2 text-[11px] font-medium text-amber-950">
                                  Import from portal — <strong>{getRecipientPortalSourceForChannel(ch)}</strong> ({label})
                                </p>
                                <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] text-amber-900/90">
                                  <span className="font-medium">Collection:</span>
                                  <label className="flex cursor-pointer items-center gap-1.5">
                                    <input
                                      type="radio"
                                      name={`portal-coll-${ch}`}
                                      className="accent-[#EC6E51]"
                                      checked={getRecipientPortalSourceForChannel(ch) === "candidates"}
                                      onChange={() =>
                                        setRecipientPortalSourceByChannel((prev) => ({
                                          ...prev,
                                          [ch]: "candidates",
                                        }))
                                      }
                                    />
                                    candidates
                                  </label>
                                  <label className="flex cursor-pointer items-center gap-1.5">
                                    <input
                                      type="radio"
                                      name={`portal-coll-${ch}`}
                                      className="accent-[#EC6E51]"
                                      checked={getRecipientPortalSourceForChannel(ch) === "users"}
                                      onChange={() =>
                                        setRecipientPortalSourceByChannel((prev) => ({
                                          ...prev,
                                          [ch]: "users",
                                        }))
                                      }
                                    />
                                    users
                                  </label>
                                  <label className="flex cursor-pointer items-center gap-1.5">
                                    <input
                                      type="radio"
                                      name={`portal-coll-${ch}`}
                                      className="accent-[#EC6E51]"
                                      checked={getRecipientPortalSourceForChannel(ch) === "approaches"}
                                      onChange={() =>
                                        setRecipientPortalSourceByChannel((prev) => ({
                                          ...prev,
                                          [ch]: "approaches",
                                        }))
                                      }
                                    />
                                    approaches
                                  </label>
                                </div>
                                <p className="mt-1 text-[10px] leading-snug text-amber-900/90">
                                  Maps name, email, phone (and username for users) to your template columns. Up to 5000
                                  documents per import.
                                </p>
                                <div className="mt-2 flex flex-wrap items-center gap-2">
                                  <button
                                    type="button"
                                    disabled={
                                      cols.length === 0 ||
                                      uploadingRecipientChannel !== null ||
                                      !addDraft ||
                                      validateCommunicationName(addDraft.communication) !== null
                                    }
                                    onClick={async () => {
                                      if (!addDraft) return;
                                      const commName = addDraft.communication.trim();
                                      const nameValidation = validateCommunicationName(commName);
                                      if (nameValidation) {
                                        setRecipientParseHint(`${label}: ${nameValidation}`);
                                        return;
                                      }
                                      const templateId = firstCronTemplateIdForChannel(cronFormRows, ch);
                                      if (!templateId) {
                                        setRecipientParseHint(`${label}: choose a template on this channel first.`);
                                        return;
                                      }
                                      setUploadingRecipientChannel(ch);
                                      setRecipientParseHint(`Importing ${label} from portal…`);
                                      try {
                                        const draftCommId = addDraft.communicationId?.trim();
                                        const data = await importPortalRecipientData({
                                          source: getRecipientPortalSourceForChannel(ch),
                                          communication: commName,
                                          name: commName,
                                          communicationKind: effectiveAddKind,
                                          channel: ch,
                                          templateId,
                                          ...(draftCommId ? { communicationId: draftCommId } : {}),
                                        });
                                        const recipientPayload = data?.recipientData as
                                          | { rows?: Record<string, unknown>[] }
                                          | undefined;
                                        const rowsFromApi = recipientPayload?.rows;
                                        const rawRows = Array.isArray(rowsFromApi) ? rowsFromApi : [];
                                        const normalizedRows: Record<string, string>[] = rawRows.map((row) =>
                                          Object.fromEntries(
                                            cols.map((c) => [c, row[c] == null ? "" : String(row[c])])
                                          ) as Record<string, string>
                                        );
                                        let returnedCommId =
                                          data?.communicationId != null && String(data.communicationId).trim() !== ""
                                            ? String(data.communicationId).trim()
                                            : undefined;
                                        if (!returnedCommId) {
                                          const commObj = data?.communication as { _id?: string } | undefined;
                                          if (commObj?._id != null) returnedCommId = String(commObj._id);
                                        }
                                        if (returnedCommId) {
                                          setAddDraft((d) => (d ? { ...d, communicationId: returnedCommId } : null));
                                        }
                                        setRecipientByChannel((p) => ({
                                          ...p,
                                          [ch]: {
                                            ...p[ch],
                                            rows: normalizedRows,
                                            csvRaw: recipientRowsToCsv(cols, normalizedRows),
                                            batchId: returnedCommId ?? null,
                                          },
                                        }));
                                        setRecipientCsvEditorOpenByChannel((p) => ({ ...p, [ch]: false }));
                                        setRecipientParseHint(
                                          `${label}: ${normalizedRows.length} row(s) from portal (${getRecipientPortalSourceForChannel(ch)}).`
                                        );
                                        toast.success(`${label}: imported from portal.`);
                                      } catch {
                                        setRecipientParseHint(`${label}: portal import failed.`);
                                      } finally {
                                        setUploadingRecipientChannel(null);
                                      }
                                    }}
                                    className="rounded-md bg-[#EC6E51] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#d85f44] disabled:cursor-not-allowed disabled:opacity-50"
                                  >
                                    {uploadingRecipientChannel === ch
                                      ? "Importing…"
                                      : `Import from portal (${label})`}
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                  </div>
                  {recipientParseHint && (
                    <p className="mt-3 text-xs text-gray-700">{recipientParseHint}</p>
                  )}
                </div>
              )}

            {addKind === "voice" && effectiveAddKind !== "cron" && voiceCallSequenceSection}

            {effectiveAddKind === "api" && addKind !== "voice" && (
              <div className="mt-4 rounded-md border border-amber-100 bg-amber-50/60 p-3 relative">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      API channels
                    </p>
                    <p className="text-xs text-gray-600">
                      Same layout as a cron rule: one column per channel (WhatsApp and Email only for now). Pick a template, then enable sends on that channel.
                    </p>
                  </div>
                </div>
                <div className="rounded-md border border-gray-200 bg-white p-3 shadow-sm">
                  <div className="grid grid-cols-1 gap-3">
                    {cronScheduleChannels.map((channel) => (
                      <div
                        key={`add-api-${channel.key}`}
                        className="rounded-md border border-gray-200 bg-gray-50/80 p-2"
                      >
                        <label className="mb-1 block text-xs font-medium text-gray-700">{channel.label}</label>
                        <div className="flex items-center gap-1">
                          <Select<TemplateSelectOption, false>
                            options={channelTemplates[channel.key].items.map((opt) => ({
                              value: opt.id,
                              label: opt.label,
                            }))}
                            value={(() => {
                              const selectedId = addDraft.templates[channel.key];
                              const selected = channelTemplates[channel.key].items.find(
                                (opt) => opt.id === selectedId
                              );
                              if (selected) return { value: selected.id, label: selected.label };
                              return selectedId ? { value: selectedId, label: selectedId } : null;
                            })()}
                            isSearchable
                            isClearable={false}
                            placeholder="Template"
                            onMenuOpen={() =>
                              loadChannelTemplates(channel.key, {
                                search: channelTemplates[channel.key].search,
                                page: 1,
                              })
                            }
                            onInputChange={(value, meta) => {
                              if (meta.action === "input-change") {
                                setChannelTemplates((prev) => ({
                                  ...prev,
                                  [channel.key]: {
                                    ...prev[channel.key],
                                    search: value,
                                  },
                                }));
                                debouncedLoadChannelTemplatesSearch(channel.key, value);
                              }
                              return value;
                            }}
                            onChange={async (val) => {
                              const templateId = val?.value || "";
                              const sel = await resolveTemplateOptionForChannel(channel.key, templateId);
                              const mergedTpn: TemplateParamNamesMap = {
                                ...(addDraft.templateParamNames ?? emptyTemplateParamNames()),
                                [channel.key]: buildTemplateParamNamesForChannel(
                                  addDraft.templateParamNames?.[channel.key],
                                  sel ?? undefined
                                ),
                              };
                              setAddDraft({
                                ...addDraft,
                                templates: { ...addDraft.templates, [channel.key]: templateId },
                                templateParamNames: mergedTpn,
                              });
                              void warnIfTemplateParamsMissing(channel.key, channel.label, templateId);
                            }}
                            className="w-full text-xs"
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 10000 }) }}
                          />
                          {isChosenTemplateId(addDraft.templates[channel.key]) && (
                            <button
                              type="button"
                              onClick={() => {
                                if (!isAddFlowRecipientChannelKey(channel.key)) return;
                                void openTemplateParamEditor(
                                  channel.key,
                                  channel.label,
                                  addDraft.templates[channel.key]
                                );
                              }}
                              title="Edit param columns"
                              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[#EC6E51]/40 bg-[#FFF5F2] text-[#9A3F2E] hover:bg-[#FDEAE5]"
                            >
                              <Wand2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                        {isChosenTemplateId(addDraft.templates[channel.key]) &&
                          (() => {
                            const sel = channelTemplates[channel.key].items.find(
                              (i) => i.id === addDraft.templates[channel.key]
                            );
                            const fallbackCol =
                              channel.key === "email"
                                ? "email"
                                : channel.key === "whatsapp" ||
                                    channel.key === "sms" ||
                                    channel.key === "rcs"
                                  ? "mobile"
                                  : "";
                            const slotCount = resolveParamSlotCount(sel);
                            const configuredParamCount = padTemplateParamNames(
                              addDraft.templateParamNames?.[channel.key],
                              slotCount
                            ).filter((x) => String(x ?? "").trim().length > 0).length;
                            const hasConfiguredAllParams = slotCount > 0 && configuredParamCount >= slotCount;
                            return (
                              <>
                                <p className="mt-1 text-[10px] leading-snug text-gray-600">
                                  {sel?.params?.length ? (
                                    <>
                                      This template expects CSV columns:{" "}
                                      <code className="rounded bg-white px-0.5">{sel.params.join(", ")}</code>
                                    </>
                                  ) : slotCount > 0 && !hasConfiguredAllParams ? (
                                    <span className="text-amber-900">
                                      Body uses <strong>{slotCount}</strong> placeholder
                                      {slotCount === 1 ? "" : "s"} (
                                      <code className="rounded bg-white px-0.5">{`{{1}}`}</code>
                                      {slotCount > 1 ? (
                                        <>
                                          {" "}
                                          …{" "}
                                          <code className="rounded bg-white px-0.5">{`{{${slotCount}}}`}</code>
                                        </>
                                      ) : null}
                                      ) but <code className="text-[10px]">params</code> are missing on this template. Add
                                      param names in Communication Automation or set column names below.
                                    </span>
                                  ) : hasConfiguredAllParams ? (
                                    <>Using configured column names for all template placeholders.</>
                                  ) : (
                                    <>
                                      No <code className="text-[10px]">params</code> on template — use{" "}
                                      <code className="rounded bg-white px-0.5">{fallbackCol || "—"}</code> in recipient
                                      data if needed
                                    </>
                                  )}
                                </p>
                              </>
                            );
                          })()}
                        <label className="mt-2 flex cursor-pointer items-center gap-2 text-xs text-gray-700">
                          <input
                            type="checkbox"
                            checked={addDraft[channel.key] === "Active"}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              if (checked && !isChosenTemplateId(addDraft.templates[channel.key])) {
                                setAddError(
                                  `Choose a ${channel.label} template before enabling this channel.`
                                );
                                return;
                              }
                              setAddError(null);
                              setAddDraft({
                                ...addDraft,
                                [channel.key]: checked ? "Active" : "Inactive",
                                ...(channel.key === "whatsapp" && !checked
                                  ? { conversational: false, webhookRoute: "" }
                                  : {}),
                              });
                            }}
                            className="h-3.5 w-3.5 rounded border-gray-300 accent-[#EC6E51]"
                          />
                          Send on this channel when your server triggers this communication
                        </label>
                        {channel.key === "whatsapp" &&
                          isChosenTemplateId(addDraft.templates.whatsapp) &&
                          renderWhatsAppConversationalFields()}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {addError && (
              <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 p-2 text-sm text-rose-700">
                {addError}
              </div>
            )}

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeAdd}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={
                  Boolean(validateCommunicationName(addDraft.communication)) ||
                  isAdding ||
                  uploadingRecipientChannel !== null ||
                  (addKind === "voice" &&
                    voiceSequence.filter((s) => s.kind === "prompt").length === 0) ||
                  (effectiveAddKind === "cron" &&
                    (cronFormRows.some((r) => !buildCronFromRow(r)) ||
                      recipientCronDataInvalid ||
                      (isVoiceCronUi &&
                        cronFormRows.some((r) => !(r.customAggregationPipeline ?? "").trim())) ||
                      (!isVoiceCronUi &&
                        cronFormRows.some((row) => {
                          const anyOn = CRON_FLOW_CHANNEL_KEYS.some((k) => row.channelActive[k]);
                          if (!anyOn) return true;
                          return CRON_FLOW_CHANNEL_KEYS.some(
                            (k) => row.channelActive[k] && !isChosenTemplateId(row.channelTemplates[k])
                          );
                        }))))
                }
                className="rounded-md bg-[#EC6E51] px-4 py-2 text-sm font-semibold text-white hover:bg-[#d85f44] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isAdding
                  ? addDraft.communicationId
                    ? "Saving..."
                    : "Adding..."
                  : addDraft.communicationId
                    ? "Save"
                    : "Add"}
              </button>
            </div>
            </form>
          </div>
        </div>
      )}

      {templateParamEditorModal && (
        <div className="fixed inset-0 z-[10003] flex items-center justify-center bg-black/50 p-4">
          <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg bg-white shadow-lg">
            <div className="border-b border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-900">Template param columns</h3>
              <p className="mt-1 text-xs text-gray-600">
                {templateParamEditorModal.channelLabel} - {templateParamEditorModal.templateLabel}
              </p>
            </div>
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
              <div>
                <p className="mb-1 text-xs font-medium text-gray-800">Template body</p>
                <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded border border-gray-200 bg-gray-50 p-2 text-[11px] text-gray-700">
                  {templateParamEditorModal.templateBody || "No body text available"}
                </pre>
              </div>
              <div>
                <p className="mb-1 text-xs font-medium text-gray-800">Params columns</p>
                <div className="space-y-2">
                  {padTemplateParamNames(
                    addDraft?.templateParamNames?.[templateParamEditorModal.channelKey],
                    templateParamEditorModal.slotCount
                  ).map((name, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="w-12 shrink-0 font-mono text-[11px] text-gray-600">{`{{${i + 1}}}`}</span>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => {
                          const v = e.target.value;
                          setAddDraft((d) => {
                            if (!d) return d;
                            const next = padTemplateParamNames(
                              d.templateParamNames?.[templateParamEditorModal.channelKey],
                              templateParamEditorModal.slotCount
                            );
                            next[i] = v;
                            return {
                              ...d,
                              templateParamNames: {
                                ...(d.templateParamNames ?? emptyTemplateParamNames()),
                                [templateParamEditorModal.channelKey]: next,
                              },
                            };
                          });
                        }}
                        placeholder="Column name"
                        className="min-w-0 flex-1 rounded border border-gray-300 px-2 py-1 text-xs outline-none focus:border-[#EC6E51] focus:ring-1 focus:ring-[#EC6E51]"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end border-t border-gray-200 p-4">
              <button
                type="button"
                onClick={() => setTemplateParamEditorModal(null)}
                className="rounded-md bg-[#EC6E51] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#d85f44]"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {templateParamsMissingModal && (
        <div className="fixed inset-0 z-[10002] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-4 shadow-lg">
            <h3 className="text-sm font-semibold text-gray-900">Template params not configured</h3>
            <p className="mt-2 text-xs leading-relaxed text-gray-700">
              <span className="font-medium text-gray-900">{templateParamsMissingModal.templateLabel}</span> (
              {templateParamsMissingModal.channelLabel}) uses{" "}
              <strong>{templateParamsMissingModal.placeholderCount}</strong> body placeholder
              {templateParamsMissingModal.placeholderCount === 1 ? "" : "s"} (
              <code className="rounded bg-gray-100 px-1">{`{{1}}`}</code>
              {templateParamsMissingModal.placeholderCount > 1 ? (
                <>
                  {" "}
                  …{" "}
                  <code className="rounded bg-gray-100 px-1">{`{{${templateParamsMissingModal.placeholderCount}}}`}</code>
                </>
              ) : null}
              ), but the template has no <code className="text-[11px]">params</code> in metadata.
            </p>
            <p className="mt-2 text-xs text-gray-600">
              Add the same number of param names to this template in Communication Automation (matching the order of{" "}
              <code className="text-[11px]">{`{{1}}`}</code>, <code className="text-[11px]">{`{{2}}`}</code>, …) so
              recipient data can be mapped when sending.
            </p>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setTemplateParamsMissingModal(null)}
                className="rounded-md bg-[#EC6E51] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#d85f44]"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {showCronTemplateWarning && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-lg bg-white p-4 shadow-lg">
            <h3 className="text-sm font-semibold text-gray-900">Templates changed for this cron</h3>
            <p className="mt-2 text-xs text-gray-700">
              You updated one or more channel templates. Make sure the cron filters / Mongo aggregation pipeline still
              match the new template params, otherwise recipient selection may break at send time.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowCronTemplateWarning(false);
                  setCronTemplateChangeAck(false);
                }}
                className="rounded-md border border-gray-300 px-3 py-1 text-xs text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCronTemplateWarning(false);
                  setCronTemplateChangeAck(true);
                  void addCommunicationRow();
                }}
                className="rounded-md bg-[#EC6E51] px-3 py-1 text-xs font-semibold text-white hover:bg-[#d85f44]"
              >
                Understood
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunicationServer;
